import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API_BASE } from "../utils/api";
import { formatCurrency, formatNumber } from "../utils/formatters";
import {
  Lock,
  Wallet,
  Users as UsersIcon,
  AlertTriangle,
  Save,
  Camera,
  TrendingDown,
  RefreshCw,
  Search,
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  Sliders,
  DollarSign,
  Building2,
  Trash2,
  History,
  ShieldCheck,
  Calendar,
  Video,
  UserPlus,
  TrendingUp,
  Briefcase,
  MessageCircle,
  Scissors,
  ClipboardCheck,
  BadgeEuro,
  ShoppingBag,
  Undo2,
  Plus,
} from "lucide-react";
import {
  PRIMARY_BTN,
  SECONDARY_BTN,
  GHOST_BTN,
  DANGER_BTN,
  FIELD_CLS,
  Badge,
  MonthPicker,
  EmptyState,
  Chip,
  COLORS,
  ICON_TILES,
  SHADOWS,
  NumberField,
  FloatingSaveBar,
  InfoHint,
  InfoTooltip,
  UserAvatar,
  toneRuolo,
  ToggleSwitch,
  ToggleChip,
  SegmentedControl,
  GHOST_ACCENT_BTN,
} from "../components/designSystem";
import { RecuperiPagamentiTab } from "../components/RecuperiPagamenti";

const PIN_KEY = "compensation_pin_session";

// Fetch wrapper che inietta PIN + token
function useCompApi() {
  const call = async (path, opts = {}) => {
    const token = localStorage.getItem("session_token");
    const pin = sessionStorage.getItem(PIN_KEY);
    const headers = {
      ...(opts.headers || {}),
      Authorization: `Bearer ${token}`,
      "X-Compensation-Pin": pin || "",
    };
    const res = await fetch(`${API_BASE}${path}`, { ...opts, headers });
    if (res.status === 401) {
      sessionStorage.removeItem(PIN_KEY);
      window.location.reload();
      return res;
    }
    return res;
  };
  return { call };
}

// Spiegazioni condivise dai tooltip della pagina.
const SORGENTE_HINT = {
  snapshot: { title: "Snapshot congelato", description: "Dati salvati alla chiusura del mese: non cambiano più anche se si modifica la configurazione." },
  live: { title: "Dati live", description: "Calcolati adesso dalla configurazione corrente: cambiano a ogni modifica finché il mese non viene congelato." },
};

const ROLE_HINTS = {
  smm: "Compenso fisso mensile per ogni cliente seguito (SMM base) più gli extra del cliente.",
  content_creator: "Tariffe di script, registrazione e montaggio, moltiplicate per le volte al mese.",
  editor: "Tariffa base editor per cliente, moltiplicata per le volte al mese.",
  videomaker: "Tariffe di registrazione e montaggio per cliente, moltiplicate per le volte al mese.",
  pm_management: "Percentuale del canone dei clienti gestiti come PM.",
  sales_commission: "Percentuale del canone mensile dei clienti venduti. Matura solo se il cliente ha pagato.",
  sales_enrollment: "Compenso una tantum per il contratto stipulato, nel mese in cui il cliente paga la prima attivazione.",
  technical_direction: "Percentuale del fatturato incassato di tutti i clienti.",
  commercial_direction: "Percentuale del fatturato incassato di tutti i clienti.",
};

function fmtEuro(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  // formatCurrency e non toLocaleString: in it-IT quello non raggruppa i numeri
  // di 4 cifre ("1850"), e l'app vuole sempre il punto (CLAUDE.md).
  return formatCurrency(v, { decimals: 0 });
}
function fmtPct(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return `${Number(v).toFixed(1)}%`;
}

function PinGate({ onOk }) {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pin) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API_BASE}/compensation/verify-pin`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pin }),
      });
      if (res.ok) {
        sessionStorage.setItem(PIN_KEY, pin);
        onOk();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "PIN non valido");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-24 scheda-font-ui">
      <div className="bg-white border border-[#e9e9f1] rounded-[18px] p-7 shadow-[0_2px_12px_rgba(0,0,0,0.03)] text-center">
        <div className="w-[46px] h-[46px] rounded-[12px] bg-[#eeeefc] text-[#5b5bd6] flex items-center justify-center mx-auto mb-4">
          <Lock className="w-5 h-5" strokeWidth={1.9} />
        </div>
        <h2 className="text-[19px] font-bold text-[#15161c]">Provvigioni & Compensi</h2>
        <p className="text-[13px] text-[#565d6d] mt-1.5 mb-6">
          Accesso riservato: inserisci il PIN di amministrazione per visualizzare i dati retributivi.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Input
              type="password"
              placeholder="Inserisci PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              data-testid="comp-pin-input"
              autoFocus
              className={`${FIELD_CLS} w-full text-center text-[16px] tracking-widest h-11`}
            />
          </div>
          <button
            type="submit"
            disabled={loading || !pin}
            data-testid="comp-pin-submit"
            className={`${PRIMARY_BTN} w-full h-11 text-[13.5px]`}
          >
            {loading ? "Verifica in corso..." : "Accedi ai compensi"}
          </button>
        </form>
      </div>
    </div>
  );
}

// Clienti attivi senza servizio: restano fuori dal Fatturato totale, che somma
// solo le fee dei servizi. Gli snapshot congelati prima di questo conteggio non
// lo hanno (`conteggio` undefined) e l'avviso non compare.
function AvvisoSenzaServizio({ conteggio, clienti }) {
  const [aperto, setAperto] = useState(false);
  if (conteggio === undefined || conteggio === null) return null;
  if (conteggio === 0) {
    return (
      <div
        className="rounded-[12px] bg-[#e7f7ee] border border-[#bfe8d0] px-4 py-2.5 flex items-center gap-2 text-[12.5px] font-medium text-[#0f7040]"
        data-testid="comp-overview-senza-servizio"
      >
        <CheckCircle2 className="w-4 h-4 shrink-0" strokeWidth={2} />
        Tutti i clienti attivi hanno un servizio attaccato
      </div>
    );
  }
  return (
    <div
      className="rounded-[12px] bg-[#fffaf0] border border-[#fbe7c2] px-4 py-3 flex flex-col gap-2.5"
      data-testid="comp-overview-senza-servizio"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-[13px] text-[#b45309]">
          <AlertTriangle className="w-4 h-4 shrink-0" strokeWidth={2} />
          <span>
            <b className="scheda-font-mono">{formatNumber(conteggio)}</b>{" "}
            {conteggio === 1 ? "cliente attivo non ha" : "clienti attivi non hanno"} un servizio attaccato
          </span>
          <InfoHint title="Clienti senza servizio" description="Non entrano nel Fatturato totale né nel calcolo di costi e compensi finché non gli si aggiunge un servizio dalla scheda cliente." />
        </span>
        {clienti.length > 0 && (
          <button type="button" onClick={() => setAperto((v) => !v)} className={GHOST_BTN}>
            {aperto ? "Nascondi elenco" : "Mostra elenco"}
          </button>
        )}
      </div>
      {aperto && (
        <div className="flex flex-wrap gap-1.5">
          {clienti.map((c) => (
            <Link
              key={c.client_id}
              to={`/scheda-cliente/${c.client_id}`}
              className="text-[12px] bg-white/80 px-2.5 py-1 rounded-[8px] border border-[#fbe7c2] text-[#15161c] hover:border-[#f59e0b]"
            >
              {c.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function OverviewTab({ api, month, readonly }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const url = month ? `/compensation/overview?month=${month}` : "/compensation/overview";
      const r = await api.call(url);
      if (r.ok) setData(await r.json());
      else toast.error("Errore caricamento overview");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [month]);

  const filtered = useMemo(() => {
    const list = data?.clients || [];
    if (!search) return list;
    return list.filter((c) => (c.name || "").toLowerCase().includes(search.toLowerCase()));
  }, [data, search]);

  const threshold = data?.config?.margin_alert_threshold_pct ?? 25;

  return (
    <div className="space-y-5 scheda-font-ui">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Fatturato totale */}
        <div className="bg-white border border-[#e9e9f1] rounded-[18px] p-5 shadow-[0_2px_12px_rgba(0,0,0,0.03)] flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider text-[#9095a1]">
              Fatturato totale
              <InfoHint title="Fatturato totale" description="Fee mensili dovute nel mese dai servizi attaccati ai clienti attivi, più la fee TikTok Shop. I clienti senza servizio non contano. «Incassato» conta solo i clienti che risultano pagati questo mese." />
            </div>
            <div className="text-[26px] font-bold text-[#15161c] scheda-font-mono tracking-tight mt-1">
              {fmtEuro(data?.totals?.revenue)}
            </div>
          </div>
          {data?.totals?.revenue_potenziale_non_incassato > 0 && (
            <div className="mt-3 pt-2.5 border-t border-[#f0f0f6] flex items-center justify-between text-[11.5px]">
              <span className="text-[#565d6d]">
                Incassato: <b className="scheda-font-mono text-[#0f7040]">{fmtEuro(data?.totals?.revenue_incassato)}</b>
              </span>
              <span className="text-[#b45309] font-medium">
                in attesa {fmtEuro(data?.totals?.revenue_potenziale_non_incassato)}
              </span>
            </div>
          )}
        </div>

        {/* Costi totali */}
        <div className="bg-white border border-[#e9e9f1] rounded-[18px] p-5 shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
          <div className="flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider text-[#9095a1]">
            Costi totali
            <InfoHint title="Costi totali" description="Compensi del team operativo e delle direzioni, costi fissi aziendali in percentuale e servizi di fornitori esterni." />
          </div>
          <div className="text-[26px] font-bold text-[#c0392b] scheda-font-mono tracking-tight mt-1">
            {fmtEuro(data?.totals?.total_cost)}
          </div>
          <div className="mt-3 text-[11.5px] text-[#9095a1]">
            Team operativo + costi esterni
          </div>
        </div>

        {/* Utile totale */}
        <div className="bg-white border border-[#e9e9f1] rounded-[18px] p-5 shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
          <div className="flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider text-[#9095a1]">
            Utile totale
            <InfoHint title="Utile totale" description="Fatturato meno costi totali, sommato su tutti i clienti." />
          </div>
          <div className="text-[26px] font-bold text-[#0f7040] scheda-font-mono tracking-tight mt-1">
            {fmtEuro(data?.totals?.utile)}
          </div>
          <div className="mt-3 text-[11.5px] text-[#9095a1]">
            Margine operativo netto
          </div>
        </div>

        {/* Marginalità media */}
        <div className="bg-white border border-[#e9e9f1] rounded-[18px] p-5 shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
          <div className="flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider text-[#9095a1]">
            Marginalità media
            <InfoHint title="Marginalità media" description="Utile totale diviso fatturato totale. I clienti sotto la soglia di alert compaiono nella tab Alert margine." />
          </div>
          <div className="text-[26px] font-bold text-[#5b5bd6] scheda-font-mono tracking-tight mt-1">
            {fmtPct(data?.totals?.margin_pct)}
          </div>
          <div className="mt-3 text-[11.5px] text-[#9095a1]">
            Soglia minima alert: {threshold}%
          </div>
        </div>
      </div>

      <AvvisoSenzaServizio
        conteggio={data?.totals?.clienti_senza_servizio}
        clienti={(data?.clients || []).filter((c) => c.ha_servizio === false)}
      />

      {/* Toolbar filtro e refresh */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-[260px] max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9095a1]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca cliente per nome..."
              className={`${FIELD_CLS} w-full pl-9 h-9 text-[13px]`}
              data-testid="comp-overview-search"
            />
          </div>
        </div>

        <div className="flex items-center gap-2.5 ml-auto">
          {data?.source && (
            <InfoTooltip {...SORGENTE_HINT[data.source === "snapshot" ? "snapshot" : "live"]}>
              <Badge tone={data.source === "snapshot" ? "warning" : "neutral"} dot>
                {data.source === "snapshot" ? "Snapshot congelato" : "Dati live"}
                {data.year_month && <span className="ml-1 scheda-font-mono">({data.year_month})</span>}
              </Badge>
            </InfoTooltip>
          )}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className={SECONDARY_BTN}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </button>
        </div>
      </div>

      {/* Clients Table */}
      <div className="bg-white border border-[#e9e9f1] rounded-[18px] shadow-[0_2px_12px_rgba(0,0,0,0.03)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-[#fafafc] border-b border-[#e9e9f1]">
              <tr>
                <th className="text-left py-3 px-4 text-[10.5px] font-semibold uppercase tracking-wider text-[#9095a1]">
                  Cliente
                </th>
                <th className="text-right py-3 px-4 text-[10.5px] font-semibold uppercase tracking-wider text-[#9095a1]">
                  <span className="inline-flex items-center gap-1">
                    Canone
                    <InfoHint title="Canone" description="Canone mensile del cliente, più la fee TikTok Shop se attiva." />
                  </span>
                </th>
                <th className="text-right py-3 px-4 text-[10.5px] font-semibold uppercase tracking-wider text-[#9095a1]">
                  <span className="inline-flex items-center gap-1">
                    Costi
                    <InfoHint title="Costi" description="Quanto costa il cliente all'agenzia nel mese: team, direzioni, costi fissi e fornitori esterni." />
                  </span>
                </th>
                <th className="text-right py-3 px-4 text-[10.5px] font-semibold uppercase tracking-wider text-[#9095a1]">
                  <span className="inline-flex items-center gap-1">
                    Utile
                    <InfoHint title="Utile" description="Canone meno costi." />
                  </span>
                </th>
                <th className="text-right py-3 px-4 text-[10.5px] font-semibold uppercase tracking-wider text-[#9095a1]">
                  <span className="inline-flex items-center gap-1">
                    Margine
                    <InfoHint title="Margine" description="Utile diviso canone. Rosso sotto la soglia di alert, giallo sotto il 50%, verde sopra." />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f0f0f6]">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center">
                    <EmptyState>Nessun cliente trovato</EmptyState>
                  </td>
                </tr>
              ) : (
                filtered.map((c) => {
                  const m = c.margin_pct;
                  return (
                    <tr
                      key={c.client_id}
                      className="hover:bg-[#fafafc] transition-colors"
                      data-testid={`comp-row-${c.client_id}`}
                    >
                      <td className="py-3 px-4 font-semibold text-[#15161c]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{c.name}</span>
                          {c.ha_pagato === false && (
                            <InfoTooltip title="Non pagato" description="Il cliente non risulta pagato questo mese: commissione vendita e direzioni maturano solo quando incassa.">
                            <span
                              className="inline-flex items-center h-[20px] px-2 rounded-full text-[10px] font-bold uppercase tracking-wide bg-[#fef3c7] text-[#b45309] border border-[#fde68a]"
                              data-testid={`comp-unpaid-${c.client_id}`}
                            >
                              NON PAGATO
                            </span>
                            </InfoTooltip>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right scheda-font-mono text-[#15161c] font-medium">
                        {fmtEuro(c.revenue)}
                        {(c.revenue_breakdown?.tiktok_agency_fee > 0 || c.revenue_breakdown?.una_tantum > 0) && (
                          <div className="text-[11px] text-[#5b5bd6] font-normal mt-0.5">
                            canone {fmtEuro(c.revenue_breakdown.canone_mensile)}
                            {c.revenue_breakdown.una_tantum > 0 && <> + una tantum {fmtEuro(c.revenue_breakdown.una_tantum)}</>}
                            {c.revenue_breakdown.tiktok_agency_fee > 0 && <> + TikTok {fmtEuro(c.revenue_breakdown.tiktok_agency_fee)}</>}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right scheda-font-mono text-[#c0392b] font-medium">
                        {fmtEuro(c.total_cost)}
                      </td>
                      <td className="py-3 px-4 text-right scheda-font-mono font-bold text-[#15161c]">
                        {fmtEuro(c.utile)}
                      </td>
                      <td className="py-3 px-4 text-right scheda-font-mono">
                        {m === null ? (
                          <span className="text-[#9095a1]">—</span>
                        ) : m < threshold ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11.5px] font-bold bg-[#fdecea] text-[#c0392b]">
                            <AlertTriangle className="w-3 h-3" />
                            {fmtPct(m)}
                          </span>
                        ) : m < 50 ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11.5px] font-bold bg-[#fef3c7] text-[#b45309]">
                            {fmtPct(m)}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11.5px] font-bold bg-[#e7f7ee] text-[#0f7040]">
                            {fmtPct(m)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Ruoli della Config per cliente, nell'ordine della card "Team sul cliente".
// `slot` è la chiave di `costs` nel calcolo backend (_compute_client_breakdown):
// l'importo mostrato in riga e nel riepilogo viene da lì, non da un calcolo
// duplicato qui. `chiavi` sono i campi di `comp` della sezione, per contare le
// modifiche non salvate.
const RUOLI_CLIENTE = [
  {
    id: "smm", enabled: "smm_enabled", user: "smm_user_id", slot: "smm",
    nome: "Social Media Manager (SMM)", breve: "SMM",
    descrizione: () => "SMM base + extra",
    tile: ICON_TILES.teal, icon: MessageCircle,
    chiavi: ["smm_enabled", "smm_user_id", "smm_extras"],
  },
  {
    id: "cc", enabled: "cc_enabled", user: "cc_user_id", services: "cc_services", mult: "cc_multiplier", slot: "content_creator",
    nome: "Content Creator", breve: "Content Creator",
    descrizione: () => "Bundle se fa script, registrazione e montaggio",
    tile: ICON_TILES.rosa, icon: Camera,
    servizi: [["script", "Script"], ["recording", "Registrazione"], ["editing", "Montaggio"]],
    chiavi: ["cc_enabled", "cc_user_id", "cc_services", "cc_multiplier"],
  },
  {
    id: "cc2", enabled: "cc2_enabled", user: "cc2_user_id", services: "cc2_services", mult: "cc2_multiplier", slot: "content_creator_2",
    nome: "Content Creator 2", breve: "Content Creator 2",
    descrizione: () => "Figura addizionale, opzionale",
    tile: ICON_TILES.rosa, icon: Camera,
    servizi: [["script", "Script"], ["recording", "Registrazione"], ["editing", "Montaggio"]],
    chiavi: ["cc2_enabled", "cc2_user_id", "cc2_services", "cc2_multiplier"],
  },
  {
    id: "editor", enabled: "editor_enabled", user: "editor_user_id", mult: "editor_multiplier", slot: "editor",
    nome: "Editor", breve: "Editor",
    descrizione: () => "Solo montaggio, indipendente dal Content Creator",
    tile: ICON_TILES.viola, icon: Scissors,
    chiavi: ["editor_enabled", "editor_user_id", "editor_multiplier"],
  },
  {
    id: "videomaker", enabled: "videomaker_enabled", user: "videomaker_user_id", services: "videomaker_services", mult: "videomaker_multiplier", slot: "videomaker",
    nome: "Videomaker", breve: "Videomaker",
    descrizione: () => "Figura terza per registrazione e/o montaggio",
    tile: ICON_TILES.arancio, icon: Video,
    servizi: [["recording", "Registrazione"], ["editing", "Montaggio"]],
    chiavi: ["videomaker_enabled", "videomaker_user_id", "videomaker_services", "videomaker_multiplier"],
  },
  {
    id: "pm", enabled: "pm_enabled", user: "pm_user_id", slot: "pm_management",
    nome: "Project Manager", breve: "PM",
    descrizione: (cfg) => `${fmtNumero(cfg?.pm_management_pct)}% del canone`,
    tile: ICON_TILES.viola, icon: ClipboardCheck,
    chiavi: ["pm_enabled", "pm_user_id"],
  },
  {
    id: "sales", enabled: "sales_commission_enabled", user: "sales_user_id", slot: "sales_commission",
    nome: "Commissione vendita", breve: "Vendita",
    descrizione: (cfg) => `${fmtNumero(cfg?.pm_sales_pct)}% del canone, solo se il cliente ha pagato`,
    tile: ICON_TILES.verde, icon: BadgeEuro, allowEmpty: true,
    chiavi: ["sales_commission_enabled", "sales_user_id"],
  },
];

const SEZIONI_EXTRA = [
  ["tiktok", ["tiktok_enabled", "tiktok_revenue_generated"]],
  ["fornitori", ["external_services"]],
];

// Voci del riepilogo che non sono ruoli della card: matureranno comunque sul
// cliente (direzioni, iscrizione) o sono costi senza persona (costi fissi, fornitori).
const VOCI_RIEPILOGO_EXTRA = [
  ["sales_enrollment", "Iscrizione commerciale"],
  ["technical_direction", "Direzione tecnica"],
  ["commercial_direction", "Direzione commerciale"],
];

function fmtNumero(v) {
  if (v === null || v === undefined || v === "") return "—";
  return formatNumber(v, { maximumFractionDigits: 1 });
}

function sezioniModificate(comp, saved) {
  if (!comp || !saved) return 0;
  const diversa = (chiavi) => chiavi.some((k) => JSON.stringify(comp[k] ?? null) !== JSON.stringify(saved[k] ?? null));
  return [...RUOLI_CLIENTE.map((r) => r.chiavi), ...SEZIONI_EXTRA.map(([, k]) => k)].filter(diversa).length;
}

// Nota sotto l'importo di un ruolo: da dove viene il numero.
function notaRuolo(r, comp, costo, nonPagato) {
  const doppio = r.mult && (comp[r.mult] || 1) > 1;
  if (r.id === "smm") return (comp.smm_extras || []).some((e) => Number(e.amount) > 0) ? "base + extra" : "al mese";
  if (r.id === "cc" || r.id === "cc2") {
    const s = comp[r.services] || {};
    if (s.script && s.recording && s.editing) return doppio ? "bundle × 2" : "bundle";
  }
  if (r.id === "sales") {
    if (!comp.sales_user_id) return "trattenuta dall'azienda";
    if (nonPagato || costo?.potential) return "in attesa di incasso";
  }
  return doppio ? "× 2 al mese" : "al mese";
}

const MARGINE_SCALA = 60; // % di margine che riempie tutta la barra

function toniMargine(pct, soglia) {
  if (pct === null || pct === undefined) return { testo: "#9095a1", barra: "#dcdce8", badge: "#f3f3f8" };
  if (pct < soglia) return { testo: "#c0392b", barra: "#e05a4a", badge: "#fdecea" };
  if (pct < soglia + 10) return { testo: "#b45309", barra: "#f59e0b", badge: "#fef3c7" };
  return { testo: "#0f7040", barra: "#10b981", badge: "#e7f7ee" };
}

function RuoloRow({ r, comp, setComp, users, cfg, costo, nonPagato, primo, readonly }) {
  const attivo = !!comp[r.enabled];
  const Icon = r.icon;
  const utente = comp[r.user];
  const importo = attivo ? (costo?.amount || costo?.potential || 0) : null;
  // L'anteprima conta la vendita anche se il cliente non ha pagato (serve alla
  // marginalità): l'arancio ricorda che il compenso matura solo all'incasso.
  const inAttesa = attivo && ((r.id === "sales" && nonPagato) || (!costo?.amount && costo?.potential));
  const set = (patch) => setComp((c) => ({ ...c, ...patch }));

  return (
    <div className={`rounded-[14px] transition-colors ${primo ? "" : "border-t border-[#f0f0f6]"} ${attivo ? "bg-white" : "bg-transparent"}`}>
      <div className="grid grid-cols-[44px_36px_minmax(0,1fr)_auto] sm:grid-cols-[44px_36px_minmax(0,1fr)_120px] gap-3 items-center p-3.5">
        <ToggleSwitch
          checked={attivo}
          onChange={(v) => set({ [r.enabled]: v })}
          disabled={readonly}
          label={r.nome}
          testId={`cfg-${r.id}-enabled`}
        />
        <div
          className="w-9 h-9 rounded-[11px] flex items-center justify-center transition-colors"
          style={attivo ? { background: r.tile.bg, boxShadow: SHADOWS.appoggiato } : { background: "#f3f3f8" }}
        >
          <Icon className="w-[17px] h-[17px]" style={{ color: attivo ? r.tile.color : "#b4b8c5" }} strokeWidth={1.9} />
        </div>
        <div className="min-w-0 flex flex-col gap-0.5">
          <span className="text-[14px] font-bold truncate" style={{ color: attivo ? "#15161c" : "#9095a1" }}>{r.nome}</span>
          <span className="text-[12px] text-[#9095a1] truncate">{r.descrizione(cfg)}</span>
        </div>
        <div className="flex flex-col items-end gap-0.5">
          {attivo ? (
            <>
              <span className="scheda-font-mono text-[15px] font-semibold" style={{ color: inAttesa ? "#b45309" : "#15161c" }}>
                {fmtEuro(importo)}
              </span>
              <span className="text-[11px] text-[#9095a1] whitespace-nowrap">{notaRuolo(r, comp, costo, nonPagato)}</span>
            </>
          ) : (
            <span className="scheda-font-mono text-[15px] font-semibold text-[#c9ccd6]">—</span>
          )}
        </div>
      </div>

      {attivo && (
        <div className="pl-3.5 sm:pl-[106px] pr-3.5 pb-3.5 flex flex-wrap items-center gap-3">
          <div className="w-full sm:w-[300px]">
            <UserSelect
              users={users}
              value={utente}
              onChange={(v) => set({ [r.user]: v })}
              allowEmpty={r.allowEmpty}
              avviso={!utente && !r.allowEmpty}
              size="md"
              testId={`cfg-${r.id}-user`}
            />
          </div>
          {r.servizi && (
            <div className="flex flex-wrap gap-2">
              {r.servizi.map(([k, l]) => (
                <ToggleChip
                  key={k}
                  checked={!!comp[r.services]?.[k]}
                  onChange={(v) => setComp((c) => ({ ...c, [r.services]: { ...(c[r.services] || {}), [k]: v } }))}
                  testId={`cfg-${r.id}-${k}`}
                >
                  {l}
                </ToggleChip>
              ))}
            </div>
          )}
          {r.mult && (
            <SegmentedControl
              value={(comp[r.mult] || 1) > 1 ? 2 : 1}
              onChange={(v) => set({ [r.mult]: v })}
              options={[
                { value: 1, label: "1× al mese", testId: `cfg-${r.id}-x1` },
                { value: 2, label: "2× al mese", testId: `cfg-${r.id}-x2` },
              ]}
            />
          )}
          {!utente && !r.allowEmpty && (
            <span className="w-full flex items-center gap-1.5 text-[12px] font-medium text-[#b45309]">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
              Assegna un utente: senza utente il compenso non viene attribuito a nessuno.
            </span>
          )}
          {r.id === "smm" && (
            <div className="w-full rounded-[12px] bg-[#fbfbfd] border border-[#eeeef4] p-3 flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-[#9095a1]">Extra SMM</span>
                <button
                  type="button"
                  onClick={() => setComp((c) => ({ ...c, smm_extras: [...(c.smm_extras || []), { service: "", amount: 0 }] }))}
                  className={`${GHOST_ACCENT_BTN} whitespace-nowrap`}
                  data-testid="cfg-smm-extra-add"
                >
                  <Plus className="w-3.5 h-3.5" /> Aggiungi extra
                </button>
              </div>
              {(comp.smm_extras || []).length === 0 ? (
                <span className="text-[12.5px] text-[#9095a1]">Nessun extra: il compenso è il solo SMM base.</span>
              ) : (
                (comp.smm_extras || []).map((e, i) => (
                  <div key={i} className="grid grid-cols-[minmax(0,1fr)_110px_32px] gap-2 items-center">
                    <input
                      type="text"
                      value={e.service || ""}
                      onChange={(ev) => setComp((c) => {
                        const arr = [...(c.smm_extras || [])];
                        arr[i] = { ...arr[i], service: ev.target.value };
                        return { ...c, smm_extras: arr };
                      })}
                      placeholder="Nome servizio extra"
                      className={`${FIELD_CLS} w-full h-[38px] text-[13px]`}
                    />
                    <NumberField
                      value={e.amount}
                      onChange={(v) => setComp((c) => {
                        const arr = [...(c.smm_extras || [])];
                        arr[i] = { ...arr[i], amount: Number(v) || 0 };
                        return { ...c, smm_extras: arr };
                      })}
                    />
                    <button
                      type="button"
                      onClick={() => setComp((c) => ({ ...c, smm_extras: (c.smm_extras || []).filter((_, j) => j !== i) }))}
                      className="w-8 h-8 rounded-[8px] flex items-center justify-center text-[#c0392b] hover:bg-[#fdecea] transition-colors"
                      aria-label="Rimuovi extra"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Filtri dell'elenco clienti della Config per cliente: uno alla volta.
const FILTRI_CLIENTI = [
  { id: "tutti", label: "Tutti", test: () => true },
  { id: "da_configurare", label: "Da configurare", test: (c) => !c.configurata },
  { id: "senza_team", label: "Senza team", test: (c) => !(c.team || []).length },
  { id: "senza_servizio", label: "Senza servizio", test: (c) => !c.ha_servizio },
];

// Stessa forma delle righe vere, così all'arrivo dei dati niente salta.
function SkeletonRigheClienti({ righe = 9 }) {
  return (
    <div className="flex flex-col gap-1 p-2" aria-busy="true" aria-label="Caricamento clienti" data-testid="comp-client-list-loading">
      {Array.from({ length: righe }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-2.5 py-2">
          <Skeleton className="bg-[#f3f3f8] w-8 h-8 rounded-[10px] shrink-0" />
          <div className="flex-1 flex flex-col gap-1.5">
            <Skeleton className="bg-[#f3f3f8] h-3 rounded-full" style={{ width: `${55 + ((i * 17) % 35)}%` }} />
            <Skeleton className="bg-[#f3f3f8] h-2.5 w-16 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

function SkeletonConfigCliente() {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-[18px] items-start" aria-busy="true" aria-label="Caricamento configurazione" data-testid="comp-client-config-loading">
      <div className="bg-white border border-[#e9e9f1] rounded-[18px] p-[22px] flex flex-col gap-4" style={{ boxShadow: SHADOWS.card }}>
        <Skeleton className="bg-[#f3f3f8] h-4 w-40 rounded-full" />
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="grid grid-cols-[44px_36px_minmax(0,1fr)_80px] gap-3 items-center">
            <Skeleton className="bg-[#f3f3f8] h-6 w-11 rounded-full" />
            <Skeleton className="bg-[#f3f3f8] h-9 w-9 rounded-[11px]" />
            <div className="flex flex-col gap-1.5">
              <Skeleton className="bg-[#f3f3f8] h-3.5 w-1/2 rounded-full" />
              <Skeleton className="bg-[#f3f3f8] h-2.5 w-1/3 rounded-full" />
            </div>
            <Skeleton className="bg-[#f3f3f8] h-4 w-full rounded-full" />
          </div>
        ))}
      </div>
      <div className="bg-white border border-[#e9e9f1] rounded-[18px] p-5 flex flex-col gap-3" style={{ boxShadow: SHADOWS.card }}>
        <Skeleton className="bg-[#f3f3f8] h-3 w-32 rounded-full" />
        <Skeleton className="bg-[#f3f3f8] h-8 w-40 rounded-[8px]" />
        <Skeleton className="bg-[#f3f3f8] h-2 w-full rounded-full" />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="bg-[#f3f3f8] h-3 w-full rounded-full" />
        ))}
      </div>
    </div>
  );
}

function ElencoClientiConfig({ clients, loading, users, selectedId, onSelect }) {
  const [ricerca, setRicerca] = useState("");
  const [filtro, setFiltro] = useState("tutti");
  const testo = ricerca.trim().toLowerCase();
  const filtroAttivo = FILTRI_CLIENTI.find((f) => f.id === filtro) || FILTRI_CLIENTI[0];
  const conteggi = useMemo(
    () => Object.fromEntries(FILTRI_CLIENTI.map((f) => [f.id, clients.filter(f.test).length])),
    [clients],
  );
  const visibili = clients.filter(
    (c) => filtroAttivo.test(c) && (!testo || (c.name || "").toLowerCase().includes(testo)),
  );
  const utente = (uid) => users.find((u) => u.user_id === uid);

  return (
    <div
      className="lg:sticky lg:top-5 bg-white border border-[#e9e9f1] rounded-[18px] flex flex-col overflow-hidden max-h-[420px] lg:max-h-[calc(100vh-40px)]"
      style={{ boxShadow: SHADOWS.card }}
      data-testid="comp-client-list"
    >
      <div className="p-3 flex flex-col gap-2.5 border-b border-[#f0f0f6]">
        <div className="flex items-center gap-2 px-1">
          <span className="text-[15px] font-bold text-[#15161c]">Clienti</span>
          {!loading && <Badge tone="neutral">{formatNumber(clients.length)}</Badge>}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-[#9095a1] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.8} />
          <Input
            value={ricerca}
            onChange={(e) => setRicerca(e.target.value)}
            onKeyDown={(e) => {
              // Invio apre il primo risultato: cerca e apri senza toccare il mouse.
              if (e.key === "Enter" && testo && visibili.length > 0) onSelect(visibili[0].client_id);
              if (e.key === "Escape") setRicerca("");
            }}
            placeholder="Cerca cliente..."
            className={`${FIELD_CLS} h-10 pl-9`}
            data-testid="comp-client-search"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FILTRI_CLIENTI.map((f) => (
            <Chip key={f.id} size="sm" accent={filtro === f.id} onClick={() => setFiltro(f.id)} testId={`comp-client-filter-${f.id}`}>
              {f.label}
              {!loading && <span className="scheda-font-mono ml-1 opacity-70">{formatNumber(conteggi[f.id])}</span>}
            </Chip>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto" style={LISTA_SCROLL_STYLE}>
        {loading ? (
          <SkeletonRigheClienti />
        ) : visibili.length === 0 ? (
          <div className="p-5 text-center">
            <EmptyState>{testo ? `Nessun cliente corrisponde a «${ricerca.trim()}»` : "Nessun cliente in questo filtro"}</EmptyState>
          </div>
        ) : (
          <div className="flex flex-col gap-0.5 p-2">
            {visibili.map((c) => {
              const scelto = c.client_id === selectedId;
              const team = (c.team || []).map(utente).filter(Boolean);
              return (
                <button
                  key={c.client_id}
                  type="button"
                  onClick={() => onSelect(c.client_id)}
                  className={`w-full text-left flex items-center gap-3 px-2.5 py-2 rounded-[12px] border transition-colors ${
                    scelto ? "bg-[#eeeefc] border-[#c9c9f0]" : "bg-transparent border-transparent hover:bg-[#f7f7fb]"
                  }`}
                  aria-current={scelto ? "true" : undefined}
                  data-testid={`comp-client-item-${c.client_id}`}
                >
                  <UserAvatar name={c.name || "?"} size={32} shape="rounded" tone={ICON_TILES.arancio} />
                  <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                    <span className={`text-[13.5px] font-semibold truncate ${scelto ? "text-[#5b5bd6]" : "text-[#15161c]"}`}>{c.name}</span>
                    <span className="flex items-center gap-1.5 text-[11.5px] text-[#9095a1]">
                      <span className="scheda-font-mono">{fmtEuro(c.fee_mensili)}</span>
                      {!c.configurata && (
                        <>
                          <span>·</span>
                          <span className="font-medium text-[#b45309]">da configurare</span>
                        </>
                      )}
                      {!c.ha_servizio && (
                        <>
                          <span>·</span>
                          <span>senza servizio</span>
                        </>
                      )}
                    </span>
                  </div>
                  {team.length > 0 && (
                    <InfoTooltip title="Team del mese" description={team.map((u) => u.name).join(", ")} side="left">
                      {/* Sovrapposti con margine negativo esplicito, non -space-x: vedi docs/11. */}
                      <span className="flex shrink-0">
                        {team.slice(0, 3).map((u, i) => (
                          <span key={u.user_id} className="rounded-full ring-2 ring-white" style={{ marginLeft: i ? -6 : 0 }}>
                            <UserAvatar name={u.name} picture={u.picture} size={20} />
                          </span>
                        ))}
                        {team.length > 3 && (
                          <span style={{ marginLeft: -6 }} className="w-5 h-5 rounded-full ring-2 ring-white bg-[#f3f3f8] text-[9.5px] font-bold text-[#565d6d] flex items-center justify-center">
                            +{team.length - 3}
                          </span>
                        )}
                      </span>
                    </InfoTooltip>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ClientConfigTab({ api, month, readonly, onDirtyChange }) {
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [users, setUsers] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [comp, setComp] = useState(null);
  const [savedComp, setSavedComp] = useState(null);
  const [client, setClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [copiedFrom, setCopiedFrom] = useState(null);
  const [anteprima, setAnteprima] = useState(null);
  const richiestaAnteprima = useRef(0);

  useEffect(() => {
    (async () => {
      const [u, g] = await Promise.all([
        api.call("/compensation/team-members"),
        api.call("/compensation/config"),
      ]);
      if (u.ok) setUsers((await u.json()).users || []);
      if (g.ok) setCfg(await g.json());
    })();
  }, []);

  // Canone e team dipendono dal mese: l'elenco si ricarica con lui.
  useEffect(() => {
    let annullata = false;
    setLoadingClients(true);
    (async () => {
      try {
        const r = await api.call(month ? `/compensation/clients?month=${month}` : "/compensation/clients");
        if (r.ok && !annullata) setClients((await r.json()).clients || []);
      } finally {
        if (!annullata) setLoadingClients(false);
      }
    })();
    return () => { annullata = true; };
  }, [month]);

  useEffect(() => {
    setJustSaved(false);
    setAnteprima(null);
    if (!selectedId) {
      setComp(null);
      setSavedComp(null);
      setClient(null);
      setCopiedFrom(null);
      return;
    }
    let annullata = false;
    setLoadingConfig(true);
    (async () => {
      try {
        const url = month
          ? `/compensation/clients/${selectedId}?month=${month}`
          : `/compensation/clients/${selectedId}`;
        const r = await api.call(url);
        if (r.ok && !annullata) {
          const d = await r.json();
          setClient(d.client);
          setComp(d.compensation);
          setSavedComp(d.compensation);
          setCopiedFrom(d.compensation?.copied_from || null);
        }
      } finally {
        if (!annullata) setLoadingConfig(false);
      }
    })();
    // Un cambio rapido di cliente non deve mostrare la config del precedente.
    return () => { annullata = true; };
  }, [selectedId, month]);

  // Anteprima del conto economico: stesso calcolo del backend usato da Overview
  // e Payout, rifatto a ogni modifica con un breve ritardo. Il contatore scarta
  // le risposte arrivate fuori ordine.
  useEffect(() => {
    if (!selectedId || !comp) return undefined;
    const id = ++richiestaAnteprima.current;
    const t = setTimeout(async () => {
      const qs = month ? `?month=${month}` : "";
      const r = await api.call(`/compensation/clients/${selectedId}/preview${qs}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(comp),
      });
      if (r.ok && id === richiestaAnteprima.current) setAnteprima((await r.json()).breakdown);
    }, 250);
    return () => clearTimeout(t);
  }, [comp, selectedId, month]);

  const nModifiche = readonly ? 0 : sezioniModificate(comp, savedComp);

  useEffect(() => {
    onDirtyChange?.(nModifiche > 0);
  }, [nModifiche, onDirtyChange]);

  useEffect(() => {
    if (nModifiche === 0) return undefined;
    const avvisa = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", avvisa);
    return () => window.removeEventListener("beforeunload", avvisa);
  }, [nModifiche]);

  const cambiaCliente = (id) => {
    if (id === selectedId) return;
    if (nModifiche > 0 && !window.confirm("Ci sono modifiche non salvate su questo cliente. Cambiare cliente senza salvarle?")) return;
    setSelectedId(id);
  };

  const save = async () => {
    if (!selectedId || !comp || readonly) return;
    setSaving(true);
    try {
      const url = month
        ? `/compensation/clients/${selectedId}?month=${month}`
        : `/compensation/clients/${selectedId}`;
      const r = await api.call(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(comp),
      });
      if (r.ok) {
        toast.success(`Configurazione salvata${month ? " per " + month : ""}`);
        setSavedComp(comp);
        setJustSaved(true);
        // L'elenco a sinistra riflette subito stato e team salvati.
        const team = [...new Set(RUOLI_CLIENTE.filter((r) => comp[r.enabled] && comp[r.user]).map((r) => comp[r.user]))];
        setClients((cs) => cs.map((c) => (c.client_id === selectedId ? { ...c, configurata: true, team } : c)));
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Errore salvataggio");
      }
    } finally {
      setSaving(false);
    }
  };

  const nomeUtente = (uid) => users.find((u) => u.user_id === uid);
  const costi = anteprima?.costs || {};
  const extServicesTotal = (comp?.external_services || []).reduce((s, x) => s + (Number(x.monthly_cost) || 0), 0);
  const ruoliAttivi = comp ? RUOLI_CLIENTE.filter((r) => comp[r.enabled]).length : 0;
  const soglia = cfg?.margin_alert_threshold_pct ?? 25;
  const margine = anteprima?.margin_pct ?? null;
  const toni = toniMargine(margine, soglia);
  const clienteScelto = clients.find((c) => c.client_id === selectedId);

  // Righe del riepilogo: ruoli attivi con un importo, poi le voci non legate a
  // un ruolo della card. Chi riceve il compenso accanto al nome della voce.
  const righeRiepilogo = [
    ...RUOLI_CLIENTE.filter((r) => comp?.[r.enabled]).map((r) => {
      const c = costi[r.slot];
      const uid = comp[r.user];
      return {
        key: r.id,
        label: r.breve,
        amount: c?.amount || 0,
        user: uid ? nomeUtente(uid) : null,
        vuoto: r.id === "sales" ? "azienda" : "non assegnato",
      };
    }),
    ...VOCI_RIEPILOGO_EXTRA.map(([slot, label]) => ({
      key: slot,
      label,
      amount: costi[slot]?.amount || 0,
      user: costi[slot]?.user_id ? nomeUtente(costi[slot].user_id) : null,
      vuoto: "azienda",
    })),
  ].filter((v) => v.amount > 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-[18px] items-start scheda-font-ui">
      <ElencoClientiConfig
        clients={clients}
        loading={loadingClients}
        users={users}
        selectedId={selectedId}
        onSelect={cambiaCliente}
      />
      <div className="flex flex-col gap-[18px] min-w-0">
        {/* Barra del cliente scelto in vetro, sopra la sua config. */}
        {selectedId && (
          <div
            className="flex flex-wrap items-center gap-x-4 gap-y-3 px-[18px] py-3.5 rounded-[18px] bg-white/[.72] border border-[rgba(233,233,241,.9)] backdrop-blur-[12px]"
            style={{ boxShadow: SHADOWS.card }}
            data-testid="comp-client-header"
          >
            <UserAvatar
              name={clienteScelto?.name || client?.name || "?"}
              size={40}
              shape="rounded"
              tone={ICON_TILES.arancio}
            />
            <div className="flex flex-col min-w-0">
              <span className="text-[17px] font-bold text-[#15161c] truncate">{clienteScelto?.name || client?.name}</span>
              <span className="text-[12px] text-[#9095a1]">
                Canone mensile{" "}
                {loadingConfig || !client
                  ? "…"
                  : <span className="scheda-font-mono font-semibold text-[#3c414d]">{fmtEuro(client.fee_mensili)}</span>}
              </span>
            </div>
            <div className="flex-1 min-w-[8px]" />
            {client && !loadingConfig && (
              <div className="flex items-center gap-2 flex-wrap">
                {month && <Badge tone="neutral">Config di {month}</Badge>}
                {copiedFrom && copiedFrom !== "legacy" && (
                  <InfoTooltip title="Config copiata" description={`Questo mese non era ancora stato configurato: la config è stata copiata da ${copiedFrom}. Salvando diventa quella di ${month}.`}>
                    <Badge tone="neutral" testId="copied-from-badge">
                      <Undo2 className="w-3 h-3 mr-1 inline" /> Copiata da {copiedFrom}
                    </Badge>
                  </InfoTooltip>
                )}
                {readonly && <Badge tone="warning" dot>Mese chiuso (readonly)</Badge>}
              </div>
            )}
          </div>
        )}

        {!selectedId ? (
          <div className="bg-white border border-[#e9e9f1] rounded-[18px] p-10 text-center" style={{ boxShadow: SHADOWS.card }}>
            <EmptyState>Scegli un cliente dall&apos;elenco per configurare chi ci lavora e quanto matura.</EmptyState>
          </div>
        ) : loadingConfig || !comp || !client ? (
          <SkeletonConfigCliente />
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-[18px] items-start">
            {/* Colonna sinistra */}
            <div className={`flex flex-col gap-[18px] min-w-0 ${readonly ? "opacity-70 pointer-events-none" : ""}`}>
              <div className="bg-white border border-[#e9e9f1] rounded-[18px]" style={{ boxShadow: SHADOWS.card }}>
                <div className="flex items-center gap-2.5 flex-wrap px-[22px] pt-[18px] pb-2">
                  <span className="text-[15px] font-bold text-[#15161c]">Team sul cliente</span>
                  <span className="h-[22px] px-[9px] rounded-full bg-[#eeeefc] text-[#5b5bd6] text-[11.5px] font-semibold flex items-center">
                    {ruoliAttivi} {ruoliAttivi === 1 ? "ruolo attivo" : "ruoli attivi"}
                  </span>
                  <div className="flex-1" />
                  <span className="flex items-center gap-1 text-[12px] text-[#9095a1]">
                    Tariffe da Config globale
                    <InfoHint title="Tariffe" description="Gli importi di ogni ruolo usano le tariffe e le percentuali della tab Config globale. Qui si sceglie chi lavora sul cliente e con quali attività." />
                  </span>
                </div>
                <div className="flex flex-col px-2 pb-2">
                  {RUOLI_CLIENTE.map((r, i) => (
                    <RuoloRow
                      key={r.id}
                      r={r}
                      comp={comp}
                      setComp={(f) => { setJustSaved(false); setComp(f); }}
                      users={users}
                      cfg={cfg}
                      costo={costi[r.slot]}
                      nonPagato={anteprima?.ha_pagato_reale === false}
                      primo={i === 0}
                      readonly={readonly}
                    />
                  ))}
                </div>
              </div>

              {/* TikTok Shop: solo per il vecchio tag ts_tiktok_shop. Con un servizio
                  del Catalogo a percentuale il fatturato si inserisce mese per mese
                  nella scheda cliente e questo valore non conta. */}
              {client?.quota_da_registro ? (
                <div className="bg-white border border-[#e9e9f1] rounded-[18px] px-5 py-4 flex items-center gap-3" style={{ boxShadow: SHADOWS.card }} data-testid="cfg-tiktok-registro">
                  <div className="w-9 h-9 rounded-[11px] flex items-center justify-center shrink-0" style={{ background: ICON_TILES.rosa.bg, boxShadow: SHADOWS.appoggiato }}>
                    <ShoppingBag className="w-[17px] h-[17px]" style={{ color: ICON_TILES.rosa.color }} strokeWidth={1.9} />
                  </div>
                  <div className="min-w-0 flex flex-col gap-0.5">
                    <span className="text-[14px] font-bold text-[#15161c]">Quota a percentuale sul fatturato</span>
                    <span className="text-[12px] text-[#565d6d]">
                      Il fatturato del cliente si inserisce mese per mese nella scheda cliente, sezione Fatturato percentuale: quello del mese scorso entra nel fatturato di questo mese.
                    </span>
                  </div>
                </div>
              ) : (
              <div className="bg-white border border-[#e9e9f1] rounded-[18px]" style={{ boxShadow: SHADOWS.card }}>
                <div className="grid grid-cols-[44px_36px_minmax(0,1fr)] gap-3 items-center px-5 py-4">
                  <ToggleSwitch
                    checked={!!comp.tiktok_enabled}
                    onChange={(v) => { setJustSaved(false); setComp((c) => ({ ...c, tiktok_enabled: v })); }}
                    label="Cliente TikTok Shop"
                    testId="cfg-tiktok-enabled"
                  />
                  <div
                    className="w-9 h-9 rounded-[11px] flex items-center justify-center"
                    style={comp.tiktok_enabled ? { background: ICON_TILES.rosa.bg, boxShadow: SHADOWS.appoggiato } : { background: "#f3f3f8" }}
                  >
                    <ShoppingBag className="w-[17px] h-[17px]" style={{ color: comp.tiktok_enabled ? ICON_TILES.rosa.color : "#b4b8c5" }} strokeWidth={1.9} />
                  </div>
                  <div className="min-w-0 flex flex-col gap-0.5">
                    <span className="text-[14px] font-bold" style={{ color: comp.tiktok_enabled ? "#15161c" : "#9095a1" }}>
                      Cliente TikTok Shop — fatturato variabile
                    </span>
                    <span className="text-[12px] text-[#9095a1]">Vale solo per i clienti con il servizio TikTok Shop</span>
                  </div>
                </div>
                {comp.tiktok_enabled && (
                  <div className="pl-5 sm:pl-[112px] pr-5 pb-4 flex flex-col gap-2">
                    <label className="text-[11.5px] font-semibold uppercase tracking-wider text-[#9095a1]">
                      Fatturato generato per il cliente questo mese
                    </label>
                    <div className="w-[200px]">
                      <NumberField
                        value={comp.tiktok_revenue_generated ?? 0}
                        onChange={(v) => { setJustSaved(false); setComp((c) => ({ ...c, tiktok_revenue_generated: Number(v) || 0 })); }}
                        testId="cfg-tiktok-revenue"
                      />
                    </div>
                    <p className="text-[12px] text-[#565d6d] max-w-2xl leading-relaxed">
                      L&apos;agenzia trattiene una percentuale configurata su questo importo. Il ricavo agenzia si somma al canone e concorre a marginalità e quote provvigionali.
                    </p>
                  </div>
                )}
              </div>
              )}

              {/* Servizi fornitori esterni */}
              <div className="bg-white border border-[#e9e9f1] rounded-[18px] px-5 py-4 flex flex-col gap-3" style={{ boxShadow: SHADOWS.card }}>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="w-9 h-9 rounded-[11px] flex items-center justify-center shrink-0" style={{ background: ICON_TILES.arancio.bg, boxShadow: SHADOWS.appoggiato }}>
                    <Building2 className="w-[17px] h-[17px]" style={{ color: ICON_TILES.arancio.color }} strokeWidth={1.9} />
                  </div>
                  <div className="flex-1 min-w-[180px] flex flex-col gap-0.5">
                    <span className="text-[14px] font-bold text-[#15161c]">Servizi fornitori esterni</span>
                    <span className="text-[12px] text-[#9095a1]">Costi sostenuti dall&apos;agenzia sul cliente: riducono il margine</span>
                  </div>
                  <span className="scheda-font-mono text-[14px] font-semibold text-[#15161c]" data-testid="cfg-ext-total">
                    {fmtEuro(extServicesTotal)}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setJustSaved(false); setComp((c) => ({ ...c, external_services: [...(c.external_services || []), { name: "", monthly_cost: 0 }] })); }}
                    className={`${SECONDARY_BTN} whitespace-nowrap flex-none`}
                    data-testid="cfg-ext-add"
                  >
                    + Aggiungi servizio
                  </button>
                </div>
                {(comp.external_services || []).length === 0 ? (
                  <span className="text-[12.5px] text-[#9095a1]">Nessun servizio esterno registrato per questo cliente.</span>
                ) : (
                  <div className="flex flex-col gap-2">
                    {(comp.external_services || []).map((s, i) => (
                      <div key={i} className="grid grid-cols-[minmax(0,1fr)_130px_32px] gap-2 items-center" data-testid={`cfg-ext-row-${i}`}>
                        <input
                          type="text"
                          value={s.name || ""}
                          onChange={(ev) => { setJustSaved(false); setComp((c) => {
                            const arr = [...(c.external_services || [])];
                            arr[i] = { ...arr[i], name: ev.target.value };
                            return { ...c, external_services: arr };
                          }); }}
                          placeholder="Nome servizio (es. Hosting, Grafica esterna, Copywriter)"
                          className={`${FIELD_CLS} w-full h-[38px] text-[13px]`}
                          data-testid={`cfg-ext-name-${i}`}
                        />
                        <NumberField
                          value={s.monthly_cost ?? 0}
                          unit="€/mese"
                          onChange={(v) => { setJustSaved(false); setComp((c) => {
                            const arr = [...(c.external_services || [])];
                            arr[i] = { ...arr[i], monthly_cost: Number(v) || 0 };
                            return { ...c, external_services: arr };
                          }); }}
                          testId={`cfg-ext-cost-${i}`}
                        />
                        <button
                          type="button"
                          onClick={() => { setJustSaved(false); setComp((c) => ({ ...c, external_services: (c.external_services || []).filter((_, j) => j !== i) })); }}
                          className="w-8 h-8 rounded-[8px] flex items-center justify-center text-[#c0392b] hover:bg-[#fdecea] transition-colors"
                          data-testid={`cfg-ext-remove-${i}`}
                          aria-label="Rimuovi servizio"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Riepilogo: conto economico del mese, fisso mentre si scorre. */}
            <div
              className="xl:sticky xl:top-5 bg-white border border-[#e9e9f1] rounded-[18px] flex flex-col"
              style={{ boxShadow: "0 2px 6px rgba(15,23,42,.08), 0 24px 48px -16px rgba(15,23,42,.30)" }}
              data-testid="cfg-riepilogo"
            >
              <div className="px-5 pt-5 pb-4 flex flex-col gap-2.5">
                <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[.06em] text-[#9095a1]">
                  Margine stimato del mese
                  <InfoHint title="Margine stimato" description="Canone meno compensi, direzioni, costi fissi aziendali e fornitori esterni, rifatto con le modifiche non ancora salvate. È una proiezione: direzioni e commissione vendita sono contate anche se il cliente non ha ancora pagato, perché matureranno all'incasso." />
                </span>
                <div className="flex items-baseline gap-2.5 flex-wrap">
                  <span className="scheda-font-mono text-[30px] font-semibold tracking-[-.02em]" style={{ color: toni.testo }}>
                    {anteprima ? fmtEuro(anteprima.utile) : "—"}
                  </span>
                  {margine !== null && (
                    <span className="h-6 px-2 rounded-full scheda-font-mono text-[12.5px] font-semibold flex items-center" style={{ background: toni.badge, color: toni.testo }}>
                      {fmtNumero(margine)}%
                    </span>
                  )}
                </div>
                <div className="relative h-2 rounded-full bg-[#f0f0f6] shadow-[inset_0_1px_2px_rgba(15,23,42,.06)]">
                  <div
                    className="h-full rounded-full transition-[width] duration-300"
                    style={{ width: `${Math.max(0, Math.min(1, (margine || 0) / MARGINE_SCALA)) * 100}%`, background: toni.barra }}
                  />
                  <span
                    className="absolute -top-1 -bottom-1 w-0.5 rounded-full bg-[#3c414d]"
                    style={{ left: `${Math.min(1, soglia / MARGINE_SCALA) * 100}%` }}
                    aria-hidden="true"
                  />
                </div>
                <span className="text-[11.5px] text-[#9095a1]">Soglia alert {fmtNumero(soglia)}%</span>
                {anteprima?.ha_pagato_reale === false && (
                  <span className="text-[11.5px]" style={{ color: COLORS.warning }} data-testid="cfg-riepilogo-non-pagato">
                    Cliente non ancora pagato questo mese: direzioni e vendita sono incluse come proiezione e matureranno all&apos;incasso.
                  </span>
                )}
              </div>

              <div className="border-t border-[#f0f0f6] px-5 py-4 flex flex-col gap-2 text-[13px]">
                <div className="flex items-center justify-between font-bold text-[#15161c]">
                  <span>Canone</span>
                  <span className="scheda-font-mono">{fmtEuro(anteprima?.revenue_breakdown?.canone_mensile ?? client.fee_mensili)}</span>
                </div>
                {anteprima?.revenue_breakdown?.una_tantum > 0 && (
                  <div className="flex items-center justify-between text-[#3c414d]">
                    <span>Attivazioni e una tantum del mese</span>
                    <span className="scheda-font-mono">+{fmtEuro(anteprima.revenue_breakdown.una_tantum)}</span>
                  </div>
                )}
                {anteprima?.revenue_breakdown?.tiktok_agency_fee > 0 && (
                  <div className="flex items-center justify-between text-[#3c414d]">
                    <span>Fee TikTok Shop</span>
                    <span className="scheda-font-mono">+{fmtEuro(anteprima.revenue_breakdown.tiktok_agency_fee)}</span>
                  </div>
                )}
                {righeRiepilogo.map((v) => (
                  <div key={v.key} className="flex items-center justify-between gap-2 text-[#3c414d]">
                    <span className="flex items-center gap-1.5 min-w-0">
                      <span className="shrink-0">{v.label} ·</span>
                      {v.user ? (
                        <>
                          <UserAvatar name={v.user.name} picture={v.user.picture} size={18} />
                          <span className="truncate">{v.user.name}</span>
                        </>
                      ) : (
                        <span className="text-[#9095a1] truncate">{v.vuoto}</span>
                      )}
                    </span>
                    <span className="scheda-font-mono shrink-0">−{fmtEuro(v.amount)}</span>
                  </div>
                ))}
                <div className="border-t border-dashed border-[#e4e4ed] my-1" />
                <div className="flex items-center justify-between text-[#3c414d]">
                  <span>Costi fissi aziendali {fmtNumero(cfg?.fixed_costs_pct)}%</span>
                  <span className="scheda-font-mono">−{fmtEuro(costi.fixed_costs?.amount || 0)}</span>
                </div>
                <div className="flex items-center justify-between text-[#3c414d]">
                  <span>Fornitori esterni</span>
                  <span className="scheda-font-mono">−{fmtEuro(costi.external_services?.amount || 0)}</span>
                </div>
              </div>

              <div className="border-t border-[#f0f0f6] px-5 py-4 flex flex-col gap-3">
                {readonly ? (
                  <Badge tone="warning" dot>Mese chiuso (readonly)</Badge>
                ) : (
                  <>
                    <span className="flex items-center gap-2 text-[12.5px] font-medium" style={{ color: nModifiche > 0 ? COLORS.warning : justSaved ? COLORS.success : COLORS.textMuted }}>
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ background: nModifiche > 0 ? COLORS.warningStrong : justSaved ? "#10b981" : "#c9ccd6" }}
                      />
                      {nModifiche > 0
                        ? `${nModifiche} ${nModifiche === 1 ? "sezione modificata" : "sezioni modificate"}`
                        : justSaved
                          ? `Configurazione salvata${month ? " per " + month : ""}`
                          : "Nessuna modifica"}
                    </span>
                    <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-2">
                      <button
                        type="button"
                        onClick={() => setComp(savedComp)}
                        disabled={nModifiche === 0 || saving}
                        className={`${SECONDARY_BTN} h-9 justify-center`}
                      >
                        Annulla
                      </button>
                      <button
                        type="button"
                        onClick={save}
                        disabled={nModifiche === 0 || saving}
                        data-testid="cfg-save"
                        className={`${PRIMARY_BTN} disabled:bg-[#eff0f7] disabled:text-[#9095a1]`}
                      >
                        <Save className="w-4 h-4" />
                        {saving ? "Salvataggio..." : "Salva configurazione"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Select utente con la foto profilo accanto al nome, sia nell'elenco sia nel
// campo chiuso: SelectValue ripete il contenuto della voce scelta. Il primo
// span del trigger è SelectValue, che shadcn tronca a una riga: qui torna flex
// per tenere avatar e nome allineati.
function UserSelect({ users, value, onChange, allowEmpty, modificato = false, avviso = false, size = "sm", testId }) {
  const alto = size === "md";
  return (
    <Select
      value={value || "__none__"}
      onValueChange={(v) => onChange(v === "__none__" ? null : v)}
    >
      <SelectTrigger
        className={`${FIELD_CLS} w-full ${alto ? "h-[38px] text-[13.5px] pl-1.5" : "h-9 text-[13px] pl-1.5"} shadow-[inset_0_1px_2px_rgba(15,23,42,.04)] hover:border-[#c9c9f0] [&>span]:!flex [&>span]:items-center [&>span]:min-w-0 ${avviso ? "border-[#f3c77a] bg-[#fffaf0]" : modificato ? "border-[#c9c9f0] bg-[#f7f7fe]" : ""}`}
        data-testid={testId}
      >
        <SelectValue placeholder="Seleziona utente…" />
      </SelectTrigger>
      <SelectContent className="scheda-font-ui">
        <SelectItem value="__none__">
          <span className="flex items-center gap-2 min-w-0">
            <span className="w-[26px] h-[26px] rounded-full border border-dashed border-[#dcdce8] shrink-0" aria-hidden="true" />
            <span className="text-[#9095a1]">{allowEmpty ? "Nessuno · resta all'azienda" : "Seleziona utente…"}</span>
          </span>
        </SelectItem>
        {users.map((u) => (
          <SelectItem key={u.user_id} value={u.user_id}>
            <span className="flex items-center gap-2 min-w-0">
              <UserAvatar name={u.name} picture={u.picture} size={26} />
              <span className="truncate">{u.name}</span>
              {u.role && <span className="text-[#9095a1] shrink-0">{u.role}</span>}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function PayoutsTab({ api, month: propMonth }) {
  const [month, setMonth] = useState(propMonth || new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    if (propMonth) setMonth(propMonth);
  }, [propMonth]);

  const ROLE_LABELS = {
    smm: "SMM",
    content_creator: "Content Creator",
    editor: "Editor",
    videomaker: "Videomaker",
    pm_management: "PM (mgmt)",
    sales_commission: "Vendita",
    sales_enrollment: "Iscrizione",
    technical_direction: "Direz. Tecnica",
    commercial_direction: "Direz. Commerciale",
  };
  const roleLabel = (r) => ROLE_LABELS[r] || r;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.call(`/compensation/user-payouts?month=${month}`);
      if (r.ok) setData(await r.json());
      else toast.error("Errore caricamento payouts");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [month]);

  const doSnapshot = async () => {
    if (!window.confirm(`Congelare snapshot del mese ${month}?`)) return;
    const r = await api.call(`/compensation/snapshot?month=${month}`, { method: "POST" });
    if (r.ok) {
      toast.success("Snapshot creato");
      load();
    } else {
      const err = await r.json().catch(() => ({}));
      toast.error(err.detail || "Errore");
    }
  };

  return (
    <div className="flex flex-col gap-5 scheda-font-ui">
      {/* Toolbar in vetro: lascia intravedere lo sfondo a gradienti dell'app. */}
      <div
        className="flex items-center gap-2.5 flex-wrap pl-[18px] pr-2.5 py-2.5 rounded-[18px] bg-white/[.72] border border-[rgba(233,233,241,.9)] backdrop-blur-[12px]"
        style={{ boxShadow: SHADOWS.card }}
      >
        <label className="text-[11.5px] font-semibold uppercase tracking-wider text-[#9095a1]">
          Mese
        </label>
        <MonthPicker
          value={month}
          onChange={setMonth}
          className={`${FIELD_CLS} w-36 h-9 shadow-[inset_0_1px_2px_rgba(15,23,42,.04)]`}
          data-testid="payout-month"
        />
        {data && (
          <InfoTooltip {...SORGENTE_HINT[data.source === "snapshot" ? "snapshot" : "live"]}>
            {data.source === "snapshot" ? (
              <Badge tone="warning" dot>Snapshot congelato</Badge>
            ) : (
              <span className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-[#e7f7ee] text-[#0f7040] text-[12px] font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-[#10b981] shadow-[0_0_0_3px_rgba(16,185,129,.18)]" />
                Live
              </span>
            )}
          </InfoTooltip>
        )}
        <div className="flex-1 min-w-[12px]" />
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className={`${SECONDARY_BTN} shadow-[0_1px_2px_rgba(15,23,42,.06)]`}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </button>
        <InfoTooltip title="Congela snapshot" description="Salva i payout del mese così come sono adesso. Lo stesso snapshot parte in automatico l'ultimo giorno del mese alle 23:59.">
          <button
            type="button"
            onClick={doSnapshot}
            data-testid="payout-snapshot"
            className={`${SECONDARY_BTN} shadow-[0_1px_2px_rgba(15,23,42,.06)]`}
          >
            <Camera className="w-3.5 h-3.5" />
            Congela snapshot
          </button>
        </InfoTooltip>
        <InfoTooltip title="Importa xlsx storico" description="Carica un report mensile in Excel per ricostruire i compensi di un mese passato.">
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            data-testid="payout-import-xlsx"
            className={`${SECONDARY_BTN} border-[#c9c9f0] text-[#5b5bd6] hover:bg-[#eeeefc] shadow-[0_1px_2px_rgba(15,23,42,.06)]`}
          >
            <Upload className="w-3.5 h-3.5" />
            Importa xlsx storico
          </button>
        </InfoTooltip>
      </div>

      <ImportMonthlyReportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        defaultMonth={month}
        onImported={(ym) => {
          setMonth(ym);
          load();
        }}
        api={api}
      />

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 md:auto-rows-fr items-stretch gap-[18px]">
          {(data.users || []).map((u) => (
            <PayoutCard key={u.user_id} u={u} roleLabel={roleLabel} />
          ))}
          {(!data.users || data.users.length === 0) && (
            <div className="col-span-full bg-white border border-[#e9e9f1] rounded-[18px] p-8 text-center" style={{ boxShadow: SHADOWS.card }}>
              <EmptyState>Nessun payout calcolato per questo mese</EmptyState>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Righe a strisce ambra: la parte di un importo che maturerà solo quando i
// clienti non pagati incasseranno. Stesso motivo nella barra "da incassare" e
// nelle barre per mansione.
const STRISCE_ATTESA = "repeating-linear-gradient(135deg,#fcd88a 0 5px,#fde9b8 5px 10px)";
const LISTA_SCROLL_STYLE = { scrollbarWidth: "thin", scrollbarColor: "#dcdce8 transparent" };

// Card di una persona nei Payout operativi. Da md in su tutte le card hanno la
// stessa altezza (auto-rows-fr). La lista Clienti riempie lo spazio che resta e
// scorre solo oltre quello: è posizionata in assoluto, così non concorre
// all'altezza della card. Altrimenti la persona con più clienti allungherebbe
// tutte le card. L'altezza la decidono le altre sezioni, con un minimo di 4 righe.
function PayoutCard({ u, roleLabel }) {
  const tono = toneRuolo(u.role);
  const conPotenziale = u.total_potential > u.total;

  // Prima i ruoli con qualcosa di incassato, poi quelli solo potenziali.
  const ruoli = [
    ...Object.entries(u.per_role || {}).map(([role, amt]) => ({ role, amt, pot: u.per_role_potential?.[role] ?? amt })),
    ...Object.entries(u.per_role_potential || {})
      .filter(([role]) => !(role in (u.per_role || {})))
      .map(([role, pot]) => ({ role, amt: 0, pot })),
  ];
  // Scala comune alle barre della persona: il ruolo più alto, potenziale compreso.
  const scala = Math.max(1, ...ruoli.map((r) => Math.max(r.amt, r.pot)));
  const clienti = u.per_client || [];

  return (
    <div
      data-testid={`payout-user-${u.user_id}`}
      className="group bg-white border border-[#e9e9f1] rounded-[18px] flex flex-col h-full scheda-font-ui transition-[transform,box-shadow] duration-[250ms] ease-[cubic-bezier(.22,.7,.3,1)] hover:-translate-y-0.5 shadow-[0_1px_2px_rgba(15,23,42,.04),0_16px_40px_-18px_rgba(15,23,42,.18)] hover:shadow-[0_2px_4px_rgba(15,23,42,.05),0_22px_48px_-18px_rgba(15,23,42,.24)] motion-reduce:transition-none motion-reduce:hover:translate-y-0"
    >
      {/* 1. Testata */}
      <div className="flex items-start gap-3 px-[22px] pt-5 pb-4">
        <UserAvatar name={u.name} picture={u.picture} size={42} shape="rounded" tone={tono} />
        <div className="flex-1 min-w-0 flex flex-col gap-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[16px] font-bold text-[#15161c] truncate">{u.name}</span>
            {u.role && (
              <span className="h-5 px-2 rounded-full text-[11px] font-semibold inline-flex items-center" style={{ background: tono.bg, color: tono.color }}>
                {u.role}
              </span>
            )}
          </div>
          {u.email && <span className="text-[12px] text-[#9095a1] truncate">{u.email}</span>}
        </div>
        <div className="flex flex-col items-end gap-0.5 shrink-0">
          <span
            className="scheda-font-mono text-[24px] font-semibold tracking-[-.02em]"
            style={{ color: u.total > 0 ? "#0f7040" : "#9095a1" }}
            data-testid={`payout-actual-${u.user_id}`}
          >
            {fmtEuro(u.total)}
          </span>
          <span className="flex items-center gap-1 text-[11.5px] text-[#9095a1]">
            incassato
            <InfoHint title="Incassato" description="Compensi già maturati nel mese: contano solo i clienti che hanno pagato." />
          </span>
        </div>
      </div>

      {/* 2. Da incassare */}
      {conPotenziale && (
        <InfoTooltip title="Da incassare" description="Quanto maturerebbe in più se tutti i clienti del mese pagassero. Arriva quando i clienti non pagati incassano, anche come recupero tardivo." block>
          <div
            className="mx-[22px] mb-4 flex-1 min-w-0 px-3.5 py-3 rounded-[12px] bg-[#fffaf0] border border-[#fbe7c2] flex flex-col gap-2"
            data-testid={`payout-potential-${u.user_id}`}
          >
            <div className="flex h-2 rounded-full overflow-hidden bg-white shadow-[inset_0_1px_2px_rgba(15,23,42,.06)]">
              <div className="bg-[#10b981]" style={{ width: `${(u.total / u.total_potential) * 100}%` }} />
              <div className="flex-1" style={{ background: STRISCE_ATTESA }} />
            </div>
            <div className="flex items-baseline gap-1.5 flex-wrap text-[12px] text-[#8a5a12]">
              <span className="scheda-font-mono font-semibold text-[#b45309]">+{fmtEuro(u.total_pending)}</span>
              <span>da incassare</span>
              <div className="flex-1" />
              <span>potenziale</span>
              <span className="scheda-font-mono font-semibold text-[#15161c]">{fmtEuro(u.total_potential)}</span>
            </div>
          </div>
        </InfoTooltip>
      )}

      {/* 3. Compenso per mansione */}
      <div className="border-t border-[#f0f0f6] px-[22px] py-3.5 flex flex-col gap-2.5">
        <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[.06em] text-[#9095a1]">
          Compenso per mansione
          <InfoHint title="Compenso per mansione" description="Totale del mese per ruolo. La parte a strisce è quanto maturerebbe se tutti i clienti pagassero. Passa sopra un ruolo per vedere come si calcola." />
        </span>
        {ruoli.map(({ role, amt, pot }) => {
          const inAttesa = pot > amt + 0.01;
          return (
            <div key={role} className="grid grid-cols-[110px_minmax(0,1fr)_auto] sm:grid-cols-[130px_minmax(0,1fr)_128px] gap-3 items-center">
              <InfoTooltip title={roleLabel(role)} description={ROLE_HINTS[role]}>
                <span className="text-[13px] font-medium text-[#3c414d] truncate">{roleLabel(role)}</span>
              </InfoTooltip>
              <div className="flex h-1.5 rounded-full bg-[#f0f0f6] overflow-hidden">
                <div className="bg-[#5b5bd6] rounded-full" style={{ width: `${(amt / scala) * 100}%` }} />
                {inAttesa && <div style={{ width: `${((pot - amt) / scala) * 100}%`, background: STRISCE_ATTESA }} />}
              </div>
              <span className="text-right scheda-font-mono text-[13px] font-semibold whitespace-nowrap" style={{ color: inAttesa ? "#b45309" : "#15161c" }}>
                {fmtEuro(amt)}
                {inAttesa && <span className="font-normal text-[#b45309]"> / {fmtEuro(pot)}</span>}
              </span>
            </div>
          );
        })}
      </div>

      {/* 4. Clienti */}
      <div className="flex-1 border-t border-[#f0f0f6] px-[22px] pt-3.5 pb-4 flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-[#9095a1]">Clienti</span>
          <span className="h-5 px-[7px] rounded-full bg-[#f3f3f8] text-[#6b7280] text-[11px] font-medium scheda-font-mono flex items-center">
            {clienti.length}
          </span>
        </div>
        {clienti.length === 0 ? (
          <span className="text-[12.5px] text-[#9095a1] py-1.5">
            Nessun cliente diretto: il compenso deriva da direzione e vendite.
          </span>
        ) : (
          <div className="relative flex-1 min-h-[136px]">
          <div
            className="absolute inset-0 flex flex-col overflow-y-auto -mr-2.5 pr-2.5"
            style={LISTA_SCROLL_STYLE}
            data-testid={`payout-clients-${u.user_id}`}
          >
            {clienti.map((pc, i) => (
              <div
                key={`${pc.client_id}-${pc.role}-${i}`}
                className="grid grid-cols-[minmax(0,1fr)_auto_72px] gap-2.5 items-center shrink-0 h-[34px] px-2 -mx-2 rounded-[8px] hover:bg-[#fbfbfd] transition-colors"
              >
                <span className="text-[13px] text-[#15161c] truncate">{pc.client_name}</span>
                <span className="h-5 px-[7px] rounded-[6px] border border-[#ececf3] bg-white text-[11px] font-medium text-[#6b7280] inline-flex items-center">
                  {roleLabel(pc.role)}
                </span>
                <span className="text-right scheda-font-mono text-[13px] font-semibold text-[#15161c]">{fmtEuro(pc.amount)}</span>
              </div>
            ))}
          </div>
          </div>
        )}
      </div>

      {/* 5. Recuperi tardivi */}
      {u.total_recovery > 0 && (
        <div
          className="mx-[22px] mb-4 rounded-[12px] bg-[#fffaf0] border border-[#fbe7c2] px-3.5 py-3 flex flex-col gap-2"
          data-testid={`payout-recovery-${u.user_id}`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 text-[11px] font-bold text-[#b45309] uppercase tracking-wider">
              <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} />
              Recuperi tardivi incassati
              <InfoHint title="Recuperi tardivi" description="Compensi di direzioni e vendita per clienti che hanno pagato in ritardo un mese precedente: si sommano al payout del mese in cui il recupero è stato registrato." />
            </span>
            <span className="scheda-font-mono text-[13.5px] font-bold text-[#b45309]">+{fmtEuro(u.total_recovery)}</span>
          </div>
          <div className="flex flex-col gap-1 max-h-[104px] overflow-y-auto -mr-2 pr-2" style={LISTA_SCROLL_STYLE}>
            {(u.recoveries || []).map((r, i) => (
              <div
                key={i}
                className="flex items-center justify-between gap-2 shrink-0 text-[12px] bg-white/80 px-2.5 py-1 rounded-[8px] border border-[#fbe7c2]"
              >
                <span className="truncate text-[#15161c]">
                  <b className="font-semibold">{r.client_name}</b>
                  <span className="text-[#9095a1] ml-1.5">· da {r.for_month} · {roleLabel(r.role)}</span>
                </span>
                <span className="scheda-font-mono text-[#b45309] font-bold shrink-0">+{fmtEuro(r.amount)}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-[#fbe7c2]">
            <span className="text-[12px] font-semibold text-[#15161c]">Totale con recuperi</span>
            <span className="scheda-font-mono text-[16px] font-bold text-[#0f7040]">{fmtEuro(u.total_with_recovery)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function AlertsTab({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.call("/compensation/alerts");
      if (r.ok) setData(await r.json());
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-4 scheda-font-ui">
      <div className="flex items-center justify-between bg-white border border-[#e9e9f1] rounded-[18px] p-4 shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-[8px] bg-[#fdecea] text-[#c0392b] flex items-center justify-center">
            <AlertTriangle className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-[15px] font-bold text-[#15161c]">
              <span className="inline-flex items-center gap-1.5">
                Clienti con margine sotto la soglia ({data?.threshold_pct ?? 25}%)
                <InfoHint title="Alert margine" description="Clienti del mese corrente il cui margine, dopo compensi, direzioni e costi, è sotto la soglia impostata in Config globale." />
              </span>
            </h3>
            <p className="text-[12px] text-[#565d6d]">
              Contratti che generano una marginalità aziendale critica dopo la remunerazione dei ruoli operativi.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className={SECONDARY_BTN}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </button>
      </div>

      {(data?.alerts || []).length === 0 ? (
        <div className="bg-white border border-[#e9e9f1] rounded-[18px] p-10 text-center">
          <div className="w-10 h-10 rounded-full bg-[#e7f7ee] text-[#0f7040] flex items-center justify-center mx-auto mb-2">
            <CheckCircle2 className="w-5 h-5" />
          </div>
          <h4 className="text-[16px] font-bold text-[#15161c]">Nessun cliente sotto la soglia di allerta</h4>
          <p className="text-[13px] text-[#565d6d] mt-1">
            Tutti i clienti attivi mantengono una marginalità aziendale sana.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {(data?.alerts || []).map((a) => (
            <div
              key={a.client_id}
              className="bg-white border border-[#e9e9f1] border-l-4 border-l-[#c0392b] rounded-[14px] p-4 shadow-sm flex items-center gap-4"
              data-testid={`alert-${a.client_id}`}
            >
              <div className="w-9 h-9 rounded-[10px] bg-[#fdecea] text-[#c0392b] flex items-center justify-center shrink-0">
                <TrendingDown className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[15px] text-[#15161c]">{a.name}</div>
                <div className="text-[12px] text-[#565d6d] mt-0.5">
                  Canone: <span className="scheda-font-mono font-semibold">{fmtEuro(a.revenue)}</span> · Utile: <span className="scheda-font-mono font-semibold">{fmtEuro(a.utile)}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[22px] font-bold text-[#c0392b] scheda-font-mono">
                  {fmtPct(a.margin_pct)}
                </div>
                <div className="text-[11px] text-[#9095a1]">margine residuo</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Campi numerici della config globale: tutto il resto (user_id dei direttori) è
// stringa. Nel form restano stringhe finché si digita, così un campo svuotato
// non diventa subito 0; tornano numeri solo al salvataggio.
const CFG_NUMERIC_KEYS = [
  "content_script", "content_recording", "content_editing", "content_full_bundle",
  "videomaker_recording", "videomaker_editing", "smm_base", "editor_base",
  "pm_management_pct", "pm_sales_pct", "commercial_enrollment_fee",
  "fixed_costs_pct", "margin_alert_threshold_pct",
  "technical_direction_pct", "commercial_direction_pct",
];
const CFG_USER_KEYS = ["technical_director_user_id", "commercial_director_user_id"];

function cfgValoreDiverso(k, a, b) {
  if (CFG_USER_KEYS.includes(k)) return (a || "") !== (b || "");
  return Number(a) !== Number(b);
}

function ConfigCard({ icon: Icon, tile, title, pill, hint, children, headerPad = "pb-2.5" }) {
  return (
    <div className="bg-white border border-[#e9e9f1] rounded-[18px] min-w-0" style={{ boxShadow: SHADOWS.card }}>
      <div className={`flex items-center gap-2.5 px-[22px] pt-[18px] ${headerPad}`}>
        <div className="w-8 h-8 rounded-[10px] flex items-center justify-center shrink-0" style={{ background: tile.bg }}>
          <Icon className="w-4 h-4" style={{ color: tile.color }} strokeWidth={1.85} />
        </div>
        <span className="text-[15px] font-bold text-[#15161c]">{title}</span>
        {hint && <InfoHint title={title} description={hint} />}
        {pill && (
          <span className="h-[22px] px-[9px] rounded-full bg-[#f3f3f8] text-[#6b7280] text-[11.5px] font-semibold flex items-center">{pill}</span>
        )}
      </div>
      {children}
    </div>
  );
}

// Riga etichetta + descrizione a sinistra, controlli a destra. Le colonne dei
// controlli arrivano da chi la usa: una sola per le quote, select + % per le
// direzioni. Sotto md i controlli vanno a capo sotto l'etichetta.
function ConfigRow({ label, description, first, cols = "md:grid-cols-[minmax(0,1fr)_104px]", children }) {
  return (
    <div className={`grid grid-cols-1 ${cols} gap-x-3.5 gap-y-2 items-center px-3.5 py-3 rounded-[12px] hover:bg-[#fbfbfd] transition-colors ${first ? "" : "border-t border-[#eeeef4]"}`}>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-[13.5px] font-semibold text-[#15161c]">{label}</span>
        {description && <span className="text-[12px] text-[#9095a1]">{description}</span>}
      </div>
      {children}
    </div>
  );
}

function ConfigTab({ api, onDirtyChange }) {
  const [cfg, setCfg] = useState(null);
  const [savedCfg, setSavedCfg] = useState(null);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const load = async () => {
    const [r, u] = await Promise.all([
      api.call("/compensation/config"),
      api.call("/compensation/team-members"),
    ]);
    if (r.ok) {
      const data = await r.json();
      setCfg(data);
      setSavedCfg(data);
    }
    if (u.ok) setUsers((await u.json()).users || []);
  };
  useEffect(() => {
    load();
  }, []);

  const chiaviModificate = useMemo(() => {
    if (!cfg || !savedCfg) return [];
    return [...CFG_NUMERIC_KEYS, ...CFG_USER_KEYS].filter((k) => cfgValoreDiverso(k, cfg[k], savedCfg[k]));
  }, [cfg, savedCfg]);
  const nModifiche = chiaviModificate.length;

  useEffect(() => {
    onDirtyChange?.(nModifiche > 0);
  }, [nModifiche, onDirtyChange]);

  // Chiudere o ricaricare la pagina con modifiche pendenti le perderebbe senza
  // avviso: il bottone di salvataggio non è più in vista in fondo al form.
  useEffect(() => {
    if (nModifiche === 0) return undefined;
    const avvisa = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", avvisa);
    return () => window.removeEventListener("beforeunload", avvisa);
  }, [nModifiche]);

  useEffect(() => {
    if (!justSaved) return undefined;
    const t = setTimeout(() => setJustSaved(false), 2500);
    return () => clearTimeout(t);
  }, [justSaved]);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const body = { ...cfg };
      CFG_NUMERIC_KEYS.forEach((k) => {
        body[k] = Number(cfg[k]) || 0;
      });
      const r = await api.call("/compensation/config", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        toast.success("Configurazione globale salvata");
        const data = await r.json();
        setCfg(data);
        setSavedCfg(data);
        setJustSaved(true);
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Errore");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return null;

  const set = (k) => (v) => {
    setJustSaved(false);
    setCfg((c) => ({ ...c, [k]: v }));
  };
  const num = (k, unit = "€") => (
    <NumberField
      value={cfg[k]}
      onChange={set(k)}
      unit={unit}
      modificato={chiaviModificate.includes(k)}
      testId={`cfg-${k}`}
    />
  );
  const nd = (
    <span className="h-[38px] flex items-center justify-end pr-[11px] rounded-[10px] text-[12px] text-[#b4b8c5] bg-[repeating-linear-gradient(135deg,#f6f6fa_0_6px,#fbfbfd_6px_12px)]">
      n/d
    </span>
  );
  const MATRICE_COLS = "grid grid-cols-[minmax(0,1fr)_repeat(4,112px)] gap-2.5 items-center px-3.5 py-2.5";

  return (
    <div className="flex flex-col gap-[18px] scheda-font-ui">
      <div className="flex flex-col gap-1 px-1">
        <h2 className="text-[18px] font-bold text-[#15161c]">Configurazione globale dei compensi</h2>
        <p className="text-[13px] text-[#565d6d]">
          Valori standard applicati di default al calcolo delle quote provvigionali e delle marginalità contrattuali.
        </p>
      </div>

      <ConfigCard icon={Video} tile={ICON_TILES.viola} title="Compensi a prestazione" hint="Tariffe fisse per attività. Si applicano a ogni cliente in cui il ruolo è attivo in Config per cliente." pill="€ per attività" headerPad="pb-3.5">
        <div className="mx-3 mb-3 rounded-[14px] border border-[#eeeef4] bg-[#fbfbfd] overflow-x-auto">
          <div className="min-w-[560px]">
            <div className={`${MATRICE_COLS} text-[11px] font-semibold uppercase tracking-[.06em] text-[#9095a1]`}>
              <span />
              <span className="text-right">Script</span>
              <span className="text-right">Registrazione</span>
              <span className="text-right">Montaggio</span>
              <span className="text-right">Bundle</span>
            </div>
            <div className={`${MATRICE_COLS} bg-white border-t border-[#eeeef4]`}>
              <span className="text-[13.5px] font-semibold text-[#15161c]">Content Creator</span>
              {num("content_script")}
              {num("content_recording")}
              {num("content_editing")}
              {num("content_full_bundle")}
            </div>
            <div className={`${MATRICE_COLS} bg-white border-t border-[#eeeef4]`}>
              <span className="text-[13.5px] font-semibold text-[#15161c]">Videomaker</span>
              {nd}
              {num("videomaker_recording")}
              {num("videomaker_editing")}
              {nd}
            </div>
            <div className="grid grid-cols-2 bg-white border-t border-[#eeeef4]">
              {[["smm_base", "SMM"], ["editor_base", "Editor"]].map(([k, label]) => (
                <div key={k} className="grid grid-cols-[minmax(0,1fr)_112px] gap-2.5 items-center px-3.5 py-2.5">
                  <span className="text-[13.5px] font-semibold text-[#15161c]">
                    {label} <span className="font-medium text-[#9095a1]">base</span>
                  </span>
                  {num(k)}
                </div>
              ))}
            </div>
          </div>
        </div>
      </ConfigCard>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-[18px]">
        <ConfigCard icon={UserPlus} tile={ICON_TILES.teal} title="Quote per cliente" hint="Percentuali e importi legati al singolo cliente: la quota del PM, la commissione mensile e l'iscrizione del commerciale.">
          <div className="flex flex-col px-2 pb-2">
            <ConfigRow first label="PM Management" description="Quota sul singolo cliente">
              {num("pm_management_pct", "%")}
            </ConfigRow>
            <ConfigRow label="Sales Commission" description="Quota mensile sul singolo cliente">
              {num("pm_sales_pct", "%")}
            </ConfigRow>
            <ConfigRow label="Commerciale Iscrizione" description="Una tantum al commerciale, alla prima attivazione pagata">
              {num("commercial_enrollment_fee")}
            </ConfigRow>
          </div>
        </ConfigCard>

        <ConfigCard icon={TrendingUp} tile={ICON_TILES.arancio} title="Marginalità" hint="Parametri per il calcolo dell'utile e per gli alert sui clienti poco redditizi.">
          <div className="flex flex-col px-2 pb-2">
            <ConfigRow first label="Costi fissi aziendali" description="Usata nel calcolo della marginalità">
              {num("fixed_costs_pct", "%")}
            </ConfigRow>
            <ConfigRow label="Soglia alert margine" description="Sotto la soglia il cliente compare in Alert margine">
              {num("margin_alert_threshold_pct", "%")}
            </ConfigRow>
          </div>
        </ConfigCard>
      </div>

      <ConfigCard icon={Briefcase} tile={ICON_TILES.viola} title="Direzioni aziendali" hint="Chi riceve le quote di direzione e con quale percentuale del fatturato incassato." headerPad="pb-1.5">
        <p className="px-[22px] md:pl-16 pb-3 text-[12.5px] leading-[1.55] text-[#565d6d]">
          Il compenso di Direzione (% × fatturato aziendale totale) viene attribuito all&apos;utente selezionato. Se un Direttore ricopre ruoli operativi sui singoli clienti, le quote si sommano automaticamente.
        </p>
        <div className="flex flex-col px-2 pb-2">
          {[
            ["Direzione Tecnica", "technical_director_user_id", "technical_direction_pct"],
            ["Direzione Commerciale", "commercial_director_user_id", "commercial_direction_pct"],
          ].map(([label, userKey, pctKey], i) => (
            <ConfigRow
              key={userKey}
              first={i === 0}
              label={label}
              description="sul fatturato aziendale totale"
              cols="md:grid-cols-[minmax(0,1fr)_280px_104px]"
            >
              <UserSelect
                users={users}
                value={cfg[userKey]}
                onChange={(v) => set(userKey)(v || "")}
                allowEmpty
                size="md"
                modificato={chiaviModificate.includes(userKey)}
                testId={`cfg-${userKey}`}
              />
              {num(pctKey, "%")}
            </ConfigRow>
          ))}
        </div>
      </ConfigCard>

      <FloatingSaveBar
        modifiche={nModifiche}
        salvato={justSaved}
        saving={saving}
        onAnnulla={() => setCfg(savedCfg)}
        onSalva={save}
        testId="cfg-save-bar"
      />
    </div>
  );
}

export default function CompensationPage({ user }) {
  const [unlocked, setUnlocked] = useState(!!sessionStorage.getItem(PIN_KEY));
  const [globalMonth, setGlobalMonth] = useState(new Date().toISOString().slice(0, 7));
  const api = useCompApi();
  const [tab, setTab] = useState("overview");
  const [configDirty, setConfigDirty] = useState(false);
  const [clientsDirty, setClientsDirty] = useState(false);

  if (!unlocked) return <PinGate onOk={() => setUnlocked(true)} />;

  // Le tab inattive vengono smontate: uscire da Config globale con modifiche
  // pendenti le perderebbe senza che la barra di salvataggio sia più visibile.
  const cambiaTab = (v) => {
    if (tab === "config" && configDirty && !window.confirm("Ci sono modifiche non salvate alla configurazione globale. Uscire senza salvarle?")) return;
    if (tab === "clients" && clientsDirty && !window.confirm("Ci sono modifiche non salvate sul cliente. Uscire senza salvarle?")) return;
    if (tab === "config") setConfigDirty(false);
    if (tab === "clients") setClientsDirty(false);
    setTab(v);
  };

  const currentYm = new Date().toISOString().slice(0, 7);
  const isPastMonth = globalMonth < currentYm;

  return (
    <div className="flex flex-col gap-6 scheda-font-ui" data-testid="compensation-page">
      {/* Header principale */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-[40px] h-[40px] rounded-[12px] bg-[#eeeefc] text-[#5b5bd6] flex items-center justify-center shrink-0">
            <Wallet className="w-5 h-5" strokeWidth={1.9} />
          </div>
          <div>
            <h1 className="text-[22px] font-bold text-[#15161c]">Provvigioni & Compensi</h1>
            <p className="text-[12.5px] text-[#565d6d]">
              Gestione quote provvigionali, payout del team, snapshot mensili e marginalità.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 ml-auto">
          <label className="text-[11.5px] font-semibold uppercase tracking-wider text-[#9095a1]">
            Mese:
          </label>
          <MonthPicker
            value={globalMonth}
            onChange={(m) => {
              // Cambiare mese ricarica la config del cliente: le modifiche aperte andrebbero perse.
              if (tab === "clients" && clientsDirty && !window.confirm("Ci sono modifiche non salvate sul cliente. Cambiare mese senza salvarle?")) return;
              setGlobalMonth(m);
            }}
            className={`${FIELD_CLS} w-44 h-9`}
            data-testid="global-month-selector"
          />
          {isPastMonth && (
            <Badge tone="warning" dot data-testid="readonly-badge">
              Mese chiuso (readonly)
            </Badge>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={cambiaTab} className="flex flex-col gap-5">
        <div className="self-start">
        <TabsList className="h-auto min-h-10 p-1 bg-white/55 backdrop-blur-[10px] border border-[rgba(233,233,241,.9)] rounded-[14px] gap-1 flex-wrap inline-flex shadow-[0_1px_2px_rgba(15,23,42,.04)]">
          <InfoTooltip title="Overview clienti" description="Costi, utile e margine di ogni cliente nel mese selezionato." side="bottom">
          <TabsTrigger
            value="overview"
            data-testid="tab-overview"
            className="data-[state=active]:bg-white data-[state=active]:text-[#15161c] data-[state=active]:shadow-[0_1px_2px_rgba(15,23,42,.06),0_4px_10px_-4px_rgba(15,23,42,.12)] text-[#565d6d] font-semibold text-[13px] rounded-[9px] px-3.5 h-8 transition-all"
          >
            Overview clienti
          </TabsTrigger>
          </InfoTooltip>
          <InfoTooltip title="Config per cliente" description="Chi lavora su ciascun cliente e con quali attività: da qui nascono i compensi." side="bottom">
          <TabsTrigger
            value="clients"
            data-testid="tab-clients"
            className="data-[state=active]:bg-white data-[state=active]:text-[#15161c] data-[state=active]:shadow-[0_1px_2px_rgba(15,23,42,.06),0_4px_10px_-4px_rgba(15,23,42,.12)] text-[#565d6d] font-semibold text-[13px] rounded-[9px] px-3.5 h-8 transition-all"
          >
            Config per cliente
          </TabsTrigger>
          </InfoTooltip>
          <InfoTooltip title="Payout operativi" description="Quanto matura ogni persona del team nel mese, per ruolo e per cliente." side="bottom">
          <TabsTrigger
            value="payouts"
            data-testid="tab-payouts"
            className="data-[state=active]:bg-white data-[state=active]:text-[#15161c] data-[state=active]:shadow-[0_1px_2px_rgba(15,23,42,.06),0_4px_10px_-4px_rgba(15,23,42,.12)] text-[#565d6d] font-semibold text-[13px] rounded-[9px] px-3.5 h-8 transition-all"
          >
            Payout operativi
          </TabsTrigger>
          </InfoTooltip>
          <InfoTooltip title="Recuperi Pagamenti" description="Clienti con mesi passati non pagati: si registra il pagamento e i compensi trattenuti tornano nel payout del mese." side="bottom">
          <TabsTrigger
            value="recoveries"
            data-testid="tab-recoveries"
            className="data-[state=active]:bg-white data-[state=active]:text-[#15161c] data-[state=active]:shadow-[0_1px_2px_rgba(15,23,42,.06),0_4px_10px_-4px_rgba(15,23,42,.12)] text-[#565d6d] font-semibold text-[13px] rounded-[9px] px-3.5 h-8 transition-all"
          >
            Recuperi Pagamenti
          </TabsTrigger>
          </InfoTooltip>
          <InfoTooltip title="Alert margine" description="Clienti con margine sotto la soglia impostata." side="bottom">
          <TabsTrigger
            value="alerts"
            data-testid="tab-alerts"
            className="data-[state=active]:bg-white data-[state=active]:text-[#15161c] data-[state=active]:shadow-[0_1px_2px_rgba(15,23,42,.06),0_4px_10px_-4px_rgba(15,23,42,.12)] text-[#565d6d] font-semibold text-[13px] rounded-[9px] px-3.5 h-8 transition-all"
          >
            Alert margine
          </TabsTrigger>
          </InfoTooltip>
          <InfoTooltip title="Config globale" description="Tariffe e percentuali di default usate in tutti i calcoli." side="bottom">
          <TabsTrigger
            value="config"
            data-testid="tab-config"
            className="data-[state=active]:bg-white data-[state=active]:text-[#15161c] data-[state=active]:shadow-[0_1px_2px_rgba(15,23,42,.06),0_4px_10px_-4px_rgba(15,23,42,.12)] text-[#565d6d] font-semibold text-[13px] rounded-[9px] px-3.5 h-8 transition-all"
          >
            Config globale
          </TabsTrigger>
          </InfoTooltip>
        </TabsList>
        </div>

        <div>
        <TabsContent value="overview" className="m-0 focus-visible:outline-none">
          <OverviewTab api={api} month={globalMonth} readonly={isPastMonth} />
        </TabsContent>
        <TabsContent value="clients" className="m-0 focus-visible:outline-none">
          <ClientConfigTab api={api} month={globalMonth} readonly={isPastMonth} onDirtyChange={setClientsDirty} />
        </TabsContent>
        <TabsContent value="payouts" className="m-0 focus-visible:outline-none">
          <PayoutsTab api={api} month={globalMonth} />
        </TabsContent>
        <TabsContent value="recoveries" className="m-0 focus-visible:outline-none">
          <RecuperiPagamentiTab api={api} user={user} />
        </TabsContent>
        <TabsContent value="alerts" className="m-0 focus-visible:outline-none">
          <AlertsTab api={api} />
        </TabsContent>
        <TabsContent value="config" className="m-0 focus-visible:outline-none">
          <ConfigTab api={api} onDirtyChange={setConfigDirty} />
        </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

// ============================================================================
// Import Monthly Report Dialog (xlsx)
// ============================================================================
function MappingSelectItem({ label, field, testid, headers, mapping, setMapping }) {
  return (
    <div>
      <label className="text-[11.5px] font-semibold uppercase tracking-wider text-[#9095a1] block mb-1.5">
        {label}
      </label>
      <Select
        value={mapping[field] || ""}
        onValueChange={(v) => setMapping((m) => ({ ...m, [field]: v }))}
      >
        <SelectTrigger className={`${FIELD_CLS} w-full h-9 text-[13px]`} data-testid={testid}>
          <SelectValue placeholder="Seleziona colonna del foglio..." />
        </SelectTrigger>
        <SelectContent className="scheda-font-ui">
          {headers.map((h) => (
            <SelectItem key={h} value={h}>
              {h}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function ImportMonthlyReportDialog({ open, onOpenChange, defaultMonth, onImported, api }) {
  const [file, setFile] = useState(null);
  const [month, setMonth] = useState(defaultMonth || new Date().toISOString().slice(0, 7));
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({
    nome: "",
    stato: "",
    importo: "",
    pagato: "",
    incassato: "",
  });
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setMonth(defaultMonth || new Date().toISOString().slice(0, 7));
      setFile(null);
      setPreview(null);
      setResult(null);
      setMapping({ nome: "", stato: "", importo: "", pagato: "", incassato: "" });
    }
  }, [open, defaultMonth]);

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.call(`/compensation/import-monthly-preview`, {
        method: "POST",
        body: fd,
      });
      if (r.ok) {
        const d = await r.json();
        setPreview(d);
        setMapping({
          nome: d.auto_map?.nome || "",
          stato: d.auto_map?.stato || "",
          importo: d.auto_map?.importo || "",
          pagato: d.auto_map?.pagato || "",
          incassato: d.auto_map?.incassato || "",
        });
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Errore lettura file");
      }
    } catch (ex) {
      toast.error("Errore lettura file");
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Carica un file");
      return;
    }
    for (const k of ["nome", "stato", "importo", "pagato", "incassato"]) {
      if (!mapping[k]) {
        toast.error(`Mappa la colonna "${k}"`);
        return;
      }
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("year_month", month);
      fd.append("col_nome", mapping.nome);
      fd.append("col_stato", mapping.stato);
      fd.append("col_importo", mapping.importo);
      fd.append("col_pagato", mapping.pagato);
      fd.append("col_incassato", mapping.incassato);
      fd.append("replace_existing", "true");
      const r = await api.call(`/compensation/import-monthly-report`, {
        method: "POST",
        body: fd,
      });
      if (r.ok) {
        const d = await r.json();
        setResult(d);
        toast.success(
          `Snapshot ${month} salvato: €${d.totals.revenue_incassato} incassato / €${d.totals.revenue} fatturato`
        );
        onImported?.(month);
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Errore import");
      }
    } catch (ex) {
      toast.error("Errore import");
    } finally {
      setImporting(false);
    }
  };

  const headers = preview?.headers || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-[22px] border-[#e9e9f1] p-6 scheda-font-ui"
        data-testid="import-monthly-dialog"
      >
        <DialogHeader className="pb-3 border-b border-[#f0f0f6]">
          <DialogTitle className="flex items-center gap-2.5 text-[18px] font-bold text-[#15161c]">
            <div className="w-8 h-8 rounded-[8px] bg-[#e7f7ee] text-[#0f7040] flex items-center justify-center">
              <FileSpreadsheet className="w-4 h-4" />
            </div>
            Importa Report Mensile (.xlsx)
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Step 1: mese */}
          <div>
            <label className="text-[11.5px] font-semibold uppercase tracking-wider text-[#9095a1] block mb-1.5">
              Mese di riferimento
            </label>
            <MonthPicker
              value={month}
              onChange={setMonth}
              className={`${FIELD_CLS} w-48 h-9`}
              data-testid="import-month"
            />
            <p className="text-[11.5px] text-[#565d6d] mt-1.5">
              Lo snapshot verrà salvato per questo mese (sovrascrive se già esistente).
            </p>
          </div>

          {/* Step 2: file */}
          <div>
            <label className="text-[11.5px] font-semibold uppercase tracking-wider text-[#9095a1] block mb-1.5">
              File Excel (.xlsx)
            </label>
            <input
              type="file"
              accept=".xlsx,.xlsm"
              onChange={handleFileChange}
              className={`${FIELD_CLS} w-full h-10 py-1.5 text-[13px] file:mr-3 file:py-1 file:px-3 file:rounded-[8px] file:border-0 file:text-[12px] file:font-semibold file:bg-[#eeeefc] file:text-[#5b5bd6] cursor-pointer`}
              data-testid="import-file-input"
            />
            {loading && (
              <p className="text-[12px] text-[#565d6d] mt-1.5 flex items-center gap-1.5">
                <RefreshCw className="w-3 h-3 animate-spin text-[#5b5bd6]" />
                Analisi del file in corso…
              </p>
            )}
          </div>

          {/* Step 3: mappatura */}
          {preview && (
            <div className="space-y-4 border-t border-[#f0f0f6] pt-4">
              <div>
                <div className="text-[14px] font-bold text-[#15161c] flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#0f7040]" />
                  {preview.total_rows} righe trovate · {preview.headers.length} colonne rilevate
                </div>
                <p className="text-[12px] text-[#565d6d] mt-0.5">
                  Verifica o modifica la corrispondenza delle colonne per l&apos;importazione corretta:
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <MappingSelectItem
                  label="NOME cliente *"
                  field="nome"
                  testid="map-nome"
                  headers={headers}
                  mapping={mapping}
                  setMapping={setMapping}
                />
                <MappingSelectItem
                  label="STATO CLIENTE *"
                  field="stato"
                  testid="map-stato"
                  headers={headers}
                  mapping={mapping}
                  setMapping={setMapping}
                />
                <MappingSelectItem
                  label="IMPORTO mensile (€) *"
                  field="importo"
                  testid="map-importo"
                  headers={headers}
                  mapping={mapping}
                  setMapping={setMapping}
                />
                <MappingSelectItem
                  label="PAGATO (SI/NO) *"
                  field="pagato"
                  testid="map-pagato"
                  headers={headers}
                  mapping={mapping}
                  setMapping={setMapping}
                />
                <MappingSelectItem
                  label="INCASSATO (SI/NO) *"
                  field="incassato"
                  testid="map-incassato"
                  headers={headers}
                  mapping={mapping}
                  setMapping={setMapping}
                />
              </div>

              {/* Preview righe */}
              <div className="border border-[#e9e9f1] rounded-[14px] overflow-hidden">
                <div className="text-[11.5px] font-bold uppercase tracking-wider bg-[#fafafc] px-3.5 py-2.5 text-[#565d6d] border-b border-[#e9e9f1]">
                  Anteprima prime 5 righe:
                </div>
                <div className="overflow-x-auto max-h-60">
                  <table className="w-full text-[11.5px]">
                    <thead className="bg-[#fafafc] border-b border-[#e9e9f1]">
                      <tr>
                        {preview.headers.slice(0, 8).map((h) => (
                          <th key={h} className="text-left px-3 py-2 font-semibold text-[#9095a1]">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#f0f0f6]">
                      {preview.preview_rows.slice(0, 5).map((row, i) => (
                        <tr key={i} className="hover:bg-[#fafafc]">
                          {preview.headers.slice(0, 8).map((h) => (
                            <td key={h} className="px-3 py-1.5 truncate max-w-[140px] text-[#3c414d]">
                              {row[h] ?? ""}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div
              className="bg-[#e7f7ee] border border-[#c6f0d6] rounded-[14px] p-4 text-[13px] space-y-1.5"
              data-testid="import-result"
            >
              <div className="font-bold text-[#0f7040] flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Snapshot {result.year_month} salvato con successo!
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-[#c6f0d6]/70">
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider text-[#565d6d]">Fatturato</div>
                  <div className="scheda-font-mono font-bold text-[#15161c]">{fmtEuro(result.totals.revenue)}</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider text-[#565d6d]">Incassato</div>
                  <div className="scheda-font-mono font-bold text-[#0f7040]">{fmtEuro(result.totals.revenue_incassato)}</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider text-[#565d6d]">Non incassato</div>
                  <div className="scheda-font-mono font-bold text-[#b45309]">{fmtEuro(result.totals.revenue_potenziale_non_incassato)}</div>
                </div>
              </div>
              <div className="text-[11.5px] text-[#565d6d] pt-1">
                {result.totals.clients_active} clienti attivi · {result.totals.clients_inactive} non attivi · {result.totals.clients_paid} pagati
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="pt-3 border-t border-[#f0f0f6] flex items-center justify-end gap-2.5">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className={SECONDARY_BTN}
          >
            Chiudi
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={!preview || importing}
            className={`${PRIMARY_BTN} bg-[#0f7040] hover:bg-[#0c5933]`}
            data-testid="import-confirm-btn"
          >
            <Upload className="w-4 h-4" />
            {importing ? "Importazione…" : "Salva Snapshot"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
